import { Backdrop, Box, Card, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material'
import HourglassTopOutlined from '@mui/icons-material/HourglassTopOutlined'
import { DefaultAccordion } from '../../generic/DefaultAccordion'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import AlertUI from '../../generic/AlertUI'
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import SurfaceResizeHandle, { SURFACE_INITIAL_HEIGHT } from './SurfaceResizeHandle'
import WorkflowSurfaceSwitch, { SURFACE_BAR_HEIGHT_PX, SURFACE_SWITCH_RIGHT_PX, SURFACE_SWITCH_SLOT_PX } from './WorkflowSurfaceSwitch'
import { apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import WorkflowFlow from './WorkflowFlow'
import { findJobNodeByJobReferenceId, toInitialCurrentNodeState } from './findJobNodeByJobReferenceId'
import ProcessTreeView from './ProcessTreeView'
import JobTask from './JobTask'
import { NodeOriginBlock } from './NodeGeneralProperties'
import NodePreparation from './NodePreparation'
import WorkflowSaveDialogs from './dialogs/WorkflowSaveDialogs'
import JobResources from './JobResources'
import { useTheme } from '@emotion/react'
import WorkflowHeader from './WorkflowHeader'
import { useEdgesState } from 'reactflow'
import { diffObjects, addIdsToDiff } from '../../generic/utilities/ObjectDiffUtil/objectDiffUtil'
import { updatePositions, validateWorkflow } from './utils'
import { useWorkflowHierarchy } from './WorkflowRecursionDetector'
import SubworkflowDialog from './SubworkflowDialog'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { getRandomUUID } from '../../generic/utilities/randomuuid'
import { WorkflowResource } from './WorkflowResource'
import { applyEdgeHighlight, applyJobHighlight, resolveHighlightTargetNodeId } from './bomTree'
import { edgesBetween } from './assemblyHighlight'
import { applyExternalMark, isExternalEffective } from './jobExternalCharacter'
import { dropNightMarkWithoutEquivalent, isNightJobFeatureEnabled, NIGHT_MARK_REMOVED_WARNING, withExplicitNightJob } from './jobNightCharacter'
import { useNightJobCensusKey } from './useNightJobCensus'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import { hasAnyOpenRow, withHostOpenState } from './openRowGuard'
import { applyBomImportMapping } from './bomImportMapping'
import { buildRevertedWorkflow } from './workflowRevert'
import { asUpdatedOperationJob, createNewJobReference } from './newJobReference'
import { clearTempDocumentScopes, collectTempDocumentUUIDs } from './tempDocumentScopes'
import { buildWorkflowSectionTitle } from './workflowSectionTitle'
import { applyCascadeApproval } from './cascadeApproval'
import { collectFeatureGroupBaselines, prepareFeatureGroupsForSave, FEATURE_GROUP_CONTROLLED_REVISIONS_SETTING, isFeatureGroupRevisionControlled } from './feature/featureGroupRevisionControl'
import { collectFeatureGroupIndexValues, collectWorkflowChildIndexValues, resolveDegradedIndexValue } from './indexDegradation'
import { useSmartInterval } from '../../generic/hooks/useSmartInterval'
import { clearUnsavedChanges, markUnsavedChanges } from '../../generic/utilities/unsavedChangesGuard'
import { isDuplicatePlanningGroupSaveError, extractDuplicatePlanningGroupValues, DUPLICATE_PLANNING_GROUP_MESSAGE_KEY } from './planningGroupSaveError'
// Historia 161 (CU6/D3/RN19-RN20/V4): reconocimiento del 409 WORKFLOW_VERSION_ALREADY_SAVED y el
// aviso con Recargar / Seguir en el editor.
import { readWorkflowVersionConflict } from './workflowVersionConflict'
import WorkflowVersionConflictDialog from './dialogs/WorkflowVersionConflictDialog'
import { findLevelNodes } from './processTree'
// Historia 152 (J41-J44, §8.9): los literales de contrato que el guardado puede rechazar.
import { readProcessSaveError } from './processApi'
// D12 (`IN16`): el par de origen se normaliza en un solo sitio del cliente (E6).
import { nodeSourceProcessPair, toSourceProcessPair } from './processOriginHighlight'
import { nextVersionNumber, shouldWarnNewVersionOnSave } from './bomLinkSaveWarning'
import { useWorkflowTemplateLinks } from './templateLinks/useWorkflowTemplateLinks'
import { applyTemplateUpdateMark, linkOfNode, newerTemplateVersionOf, NO_UNIT_STATE, pendingUpdateOf, publishBaseVersionOf, rootLinkOf, templateDecision, templateUpdateIntent, templateUpdateNodeIds, unitIdOf, viewedUnitLinks, withTemplateDecision, withTemplateUpdate } from './templateLinks/templateLinks'
import { deletedUnitNodes, discardedOperationJobCodes, findNodeDeep, materializeTemplateUpdate, withoutUpdatesOfDeletedNode } from './templateLinks/templateMaterialization'
import { isUnitEdited, rootBomAndDocumentsEdited, unitContentOf } from './templateLinks/templateEdits'
import TemplateLinkDecisionDialog from './templateLinks/TemplateLinkDecisionDialog'
import TemplateLinkLabel from './templateLinks/TemplateLinkLabel'
import TemplateUpdateBanner from './templateLinks/TemplateUpdateBanner'
import TemplateUpdateImpactDialog from './templateLinks/TemplateUpdateImpactDialog'
import { dismissTemplateLink, getTemplateTargetWorkflow, getTemplateUpdateImpact, toTemplateLinkChangesBody, toTemplateUpdatesBody } from './templateLinks/templateLinkApi'
import { isTemplateLinkError, translateTemplateLinkError } from './templateLinks/templateLinkErrors'
import { linkWorkflowBom, readBomLibraryError, updateWorkflowBom } from '../bomLibrary/bomLibraryApi'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
// Historia 130: la vista OPERATIONS del editor y su wrapper único de las rutas de Sun.
import OperationLibraryTable from './OperationLibraryTable'
import OperationSaveModeDialog from './OperationSaveModeDialog'
import OperationPendingChangesDialog from './OperationPendingChangesDialog'
import { commitOperationScope, discardOperationScope, dismissOperationUpdate, getOperationDetail, getOperationVersionDetail, getOperationVersionSaveMode, getWorkflowOperationLinks, readOperationLibraryError } from './operationLibraryApi'
// Historia 184 (CA7/CA8): el aviso de "versión no vigente" de la vista OPERATIONS y su regla pura.
import OperationVersionNotice from './OperationVersionNotice'
import { currentVersionNumberOf } from './operationVersionNoticeRule'

/** contrato-sun.md §4: the two 409 literals of N16 that mean "confirm and retry", not "this failed". */
const BOM_LINK_REPLACEMENT_LITERALS = [
  'Workflow has an embedded BOM; linking will discard it',
  'Workflow is already linked to a BOM version'
]

/** Story 224: the template-update impact dialog when it is closed (the unit's link, E3's impact, and whether it only shows it). */
const NO_TEMPLATE_IMPACT_DIALOG = { impact: {}, link: {}, viewOnly: false, discardedOperationJobs: [] }
// HU-05: the question "publish or unlink" is closed while it has no rows.
const NO_DECISION_DIALOG = { rows: [], single: false }
// What a fresh snapshot of the workflow (load, save, revert) holds of the template state of the editor: nothing pending, no baselines.
const NO_TEMPLATE_STATE = { templateUpdates: [], templateDecisions: [], unitBaselines: {}, insertedTemplateUnits: {} }

/**
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowEditingDataAction} WorkflowEditingDataAction EditingDataAction object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowEditingData} WorkflowEditingData EditingData object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').TechnologyNodeData} TechnologyNodeData JobReference object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow Workflow object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').GeneratedWorkflow} GeneratedWorkflow GeneratedWorkflow object
 */

const Workflow = (
  {
    initialWorkflow = null,
    // Historia 170 (S90, J4a): the job to select once this workflow loads (from the document
    // repository's "Open associated record", `MainWorkflows.jsx`'s `state.jobReferenceId`). Absent
    // for every OTHER caller (S91 de esta historia) — nothing is selected, unchanged from before.
    // `''` (never `null`/`undefined`, CLAUDE.md — this file is business logic, not `generic/`).
    initialJobReferenceId = '',
    setInitialWorkflow,
    hideButtonsSubWorkflow = false,
    frontendConfig,
    onWorkflowChange,
    startInEditMode = false,
    parentWorkflowId = null,
    // Ajuste PO 2026-07-27: identity of the workflow this one hangs from, shown in the graph
    // section's title so an open subworkflow says which workflow it belongs to. Empty at top level.
    parentWorkflowCode = '',
    parentWorkflowName = '',
    nodes,
    setNodes,
    onNodesChange,
    parentPartReference = null,
    // RN117.2 (story #150): reports whether this editor would accept a save right now, for the hosts
    // that wrap it in a dialog of their own.
    onSaveAllowedChange = null,
    onEditModeChange,
    // Comes from FabricationOrder.jsx window (To act like a workflow, not a subworkflow)
    fromFO,
    allowEdit,
    // S31 (RN17/D7): whether the FabricationOrder admits execution (not closed/cancelled).
    // Templates/subworkflows have no FabricationOrder, so they default to operable.
    fabricationOrderOperable = true,
    // Historia 118/RN4: the read-only process Tree surface is OPT-IN, and only the Workflows screen
    // opts in. The fabrication-order window and the subworkflow dialog keep the editor they always
    // had — not because a flag hides the lens, but because nobody asks for it there (S93/S94).
    showProcessTree = false,
    // Story 224 (S111): inside a `SubworkflowDialog` the links of the TOP-LEVEL workflow arrive here ({ latestVersion, links,
    // reload, unitNodeId, actions }); this editor then reads no E1 of its own (its id is a subworkflow's, not a piece workflow's)
    // and paints no root link. Its banner speaks only for `unitNodeId`, the subworkflow being viewed, and its Update/Dismiss/View
    // changes are the top-level editor's `actions` (E2/E3 take the top-level workflow's id, and its Save is what writes an update).
    inheritedTemplateLinks = {}
  }
) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  // Story 183 (C4, RN6.3): read from the context and not from the `frontendConfig` prop, because not
  // every window that mounts this editor passes that prop (the FO detail dialog does not).
  const [contextFrontEndConfig] = useFrontEndConfig()
  const nightJobFeatureEnabled = isNightJobFeatureEnabled(contextFrontEndConfig)
  // RN2/RN3: which surface the content area shows. It is governed HERE, not remembered inside the
  // switch, and every process opens on the graph.
  const [surface, setSurface] = useState('graph')
  const [detailedBranches, setDetailedBranches] = useState(true)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  // Historia 161-serialize-concurrent-workflow-saves-to-stop-d (RN15/RN16/RN18, DL6): a single save
  // "in flight" for every save-triggering action of the editor (header Save, dialog SAVE, "tasks
  // without program"/cascade acceptance, the alta POST). `savingRef` is the SYNCHRONOUS guard: it
  // is checked/set in the very first line of a click handler, before any `await`, so a second click
  // landing before repaint sees the lock already taken (RN16). `isSaving` is its render-visible
  // twin, read by `WorkflowHeader` (disabled Save/Revert, RN17/DL6), by `WorkflowSaveDialogs`
  // (disabled SAVE, RN15) and by the saving overlay below, which shows the spinner and `Saving`.
  //
  // `beginSave` only RESERVES the save (the synchronous guard). The screen lock (`isSaving`: the
  // overlay, the inert editor, the disabled buttons) is switched on by `saveWorkflow` itself, the
  // function that actually saves — so nothing is locked while a dialog derived from SAVE ("tasks
  // without programs", cascade) is still waiting for the user's answer.
  const savingRef = useRef(false)
  // The links of E1 as the last render saw them: the reducer (G4) follows their chains of parents to retire the updates of a deleted unit.
  const templateLinksRef = useRef([])
  const [isSaving, setIsSaving] = useState(false)
  const beginSave = () => {
    if (savingRef.current) {
      return false
    }
    savingRef.current = true
    return true
  }
  const endSave = () => {
    savingRef.current = false
    setIsSaving(false)
  }
  // Enmienda 2 (post-GATE 2, RN35/RN36): whether THIS editor instance is still mounted. Leaving the
  // editor does not cancel a save in flight (E2-D2) — the promise chain in `saveWorkflow` keeps
  // running after unmount, so every step that touches the UI (alerts, `dispatchWorkflowData`,
  // `clearUnsavedChanges`, `updateIdValues`/`setNodes`, the reload `GET`, the D3 dialog) checks this
  // ref after its `await` and skips itself once it goes `false`; the steps that reach the server
  // (`workflowChangeLog`, `unLock`) run all the same. A fresh mount gets a fresh ref (and a fresh
  // `isSaving`/`savingRef`, since both are plain component state): a remounted editor always starts
  // with no save in flight, with no extra code needed for that.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  // Enmienda 2 (RN31/RN32): the container the `Backdrop` is contained in (MUI's default is
  // `position: fixed`, which would cover the whole window and break "salir está permitido", E2-D2)
  // and whose subtree becomes `inert` while a save is in flight, so neither a click/drag/wheel nor
  // the keyboard (including a focus already inside it, and the tabbing that could reach it) can
  // reach any control of the editor (RN32). React 18 has no `inert` prop, so it is set imperatively.
  const editorContentRef = useRef(null)
  // #215: one height for the graph and the Tree, set only by dragging the bar under them (Workflows screen).
  // Elsewhere there is no bar, so it stays at 50vh.
  const [surfaceHeight, setSurfaceHeight] = useState(SURFACE_INITIAL_HEIGHT)
  const graphBoxRef = useRef(null)
  // #167 (PO, 29/09/2026): a double click on an operation of the graph's panel opens its "General Properties", on one
  // of its material lines its "Resources" — each click on the panel is a new request to open the accordion.
  const [generalPropertiesSignal, setGeneralPropertiesSignal] = useState(0)
  const [resourcesSignal, setResourcesSignal] = useState(0)
  const openJobProperties = useCallback(() => setGeneralPropertiesSignal((signal) => signal + 1), [])
  const openJobResources = useCallback(() => setResourcesSignal((signal) => signal + 1), [])
  useEffect(() => {
    const content = editorContentRef.current
    if (!content) {
      return
    }
    if (isSaving) {
      if (content.contains(document.activeElement)) {
        document.activeElement.blur()
      }
      content.setAttribute('inert', '')
    } else {
      content.removeAttribute('inert')
    }
  }, [isSaving])
  // Historia 161 (CU6/D3): the version-conflict warning (`WORKFLOW_VERSION_ALREADY_SAVED`, RN19).
  // `open: false` is the "no conflict" default (avoiding `null`/`undefined`, CLAUDE.md); the
  // `latestWorkflowId`/`latestVersion` values are meaningless while `open` is `false`.
  const [workflowVersionConflict, setWorkflowVersionConflict] = useState({ open: false, latestWorkflowId: 0, latestVersion: 0 })
  /*
   * RN117.3 (story #150): the SET of hosts holding an open row, not a boolean. Three screens mount task
   * and resource tables at once; with one flag the host that closed last cleared it for everyone,
   * unblocking a save another host still needed blocked (R12).
   */
  const [openRowHosts, setOpenRowHosts] = useState(() => new Set())
  const reportOpenRow = useCallback((hostId, isOpen) => {
    setOpenRowHosts((previous) => withHostOpenState(previous, hostId, isOpen))
  }, [])
  const saveAllowed = !hasAnyOpenRow(openRowHosts)

  /*
   * RN117.2: the two hosts that wrap this editor — the fabrication order window and the subworkflow
   * dialog — have their own accept button, and it has to answer to the same rule. They are told, not
   * left to guess.
   */
  useEffect(() => {
    onSaveAllowedChange?.(saveAllowed)
  }, [saveAllowed, onSaveAllowedChange])
  const [updateReactFlow, setUpdateReactFlow] = useState(true)
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [progressCalculationTypes, setProgressCalculationTypes] = useState([])
  const [resourceTypes, setResourceTypes] = useState([])
  const [firstLoad, setFirstLoad] = useState(true)
  const [originalData, setOriginalData] = useState('')
  const [diff, setDiff] = useState(null)
  // HU-13/S44: the BOM components the last import removed, kept so the removal stays visible in
  // red until the workflow is saved or reloaded. Owned here — NOT inside `WorkflowResource` —
  // because the BOM accordion mounts with `unmountOnExit`: that component is destroyed and rebuilt
  // every time the accordion collapses, a JOB node is selected or a subworkflow is opened, which
  // would silently drop the removals from the view.
  const [removedWorkflowResources, setRemovedWorkflowResources] = useState([])
  // S31/S32 (ajuste PO 2026-07-27): the BOM component whose job chip produced the current graph
  // highlight, so the tree can mark that row in the node's own blue.
  const [highlightedResourceUUID, setHighlightedResourceUUID] = useState('')
  // Story 117/S86: the workflow's BOM link, as the BOM section reads it (N15). Kept here — not in
  // `WorkflowResource` — because the save lives here and the BOM accordion mounts with
  // `unmountOnExit`: the warning must be available even with the section collapsed.
  const [bomLink, setBomLink] = useState({})
  const [bomNewVersionWarning, setBomNewVersionWarning] = useState(false)
  // Historia 130 (S7/S9): la versión de biblioteca que la vista OPERATIONS tiene cargada. Su
  // `jobReference` es el DTO del editor reutilizado VERBATIM (N3), de modo que los cuatro
  // acordeones lo pintan sin ficha propia.
  const [selectedOperationVersion, setSelectedOperationVersion] = useState({})
  // S22 (RN-02.11, G1/D-13 a): el ámbito de edición de la vista OPERATIONS, SEPARADO del
  // `tempWorkflowUUID` del editor. Solo el commit (N8) escribe; el revert (N9) lo tira entero.
  const [operationScopeUUID, setOperationScopeUUID] = useState(() => getRandomUUID())
  const [operationSaveMode, setOperationSaveMode] = useState(null)
  const [pendingVersionChange, setPendingVersionChange] = useState(null)
  const [operationReloadKey, setOperationReloadKey] = useState(0)
  // Ajuste del PO (2026-09-15): el literal con el que Sun rechazó el alta al guardar, para que el
  // diálogo vuelva con él y con lo que el usuario ya había escrito.
  const [operationCreateError, setOperationCreateError] = useState('')
  // S85 (CA5.1, N13): el estado de vínculo de CADA nodo del workflow, leído en una sola llamada.
  // Es la fuente del aviso: resolverlo nodo a nodo sería una consulta por nodo.
  const [operationLinks, setOperationLinks] = useState({})
  const theme = useTheme()
  const [subworkflowData, setSubworkflowData] = useState(null)
  const [subworkflowNodeId, setSubworkflowNodeId] = useState(null)
  const isUserAuthorized = useAuthorization()
  /** @type {[import('../../generic/utilities/objectStructures/globalStructures').IdName[], React.Dispatch<SetStateAction<import('../../generic/utilities/objectStructures/globalStructures').IdName[]>>]} */
  const [indexValues, setIndexValues] = useState([])
  const [workflowUseIndex, setWorkflowUseIndex] = useState()
  // S16 (HU-05/RN05.1, enmienda 4 GATE 2): cascade approval modal is only offered when BOTH
  // USE_WORKFLOW_INDEX and WORKFLOW_CASCADE_APPROVAL are active — same settings path jupiter
  // already uses for USE_WORKFLOW_INDEX (settings/{KEY}), no dedicated endpoint (sun M5-correction).
  const [cascadeApprovalEnabled, setCascadeApprovalEnabled] = useState()
  // Story 186 (S70, C1): CONTROLLED feature group revisions. Read from its own setting (never from
  // useFrontEndConfig) and fail-closed: an error or a missing value means FREE mode.
  const [featureGroupRevisionControlled, setFeatureGroupRevisionControlled] = useState(false)
  // Story 208 (S30): the optional machine time column of the task table. Read from its own setting and
  // fail-closed: an error or a missing value means OFF. Every screen that mounts <Workflow> inherits it
  // from here (S32), so none of them needs a prop of its own.
  const [showMachineTime, setShowMachineTime] = useState(false)

  // Use the hierarchy hook
  const hierarchy = useWorkflowHierarchy()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await apiGet('progressCalculationTypes')
        const result = await apiGet('processResourceTypes')
        const indexValues = await apiGet('indexValues/available')
        const useIndex = await apiGet('settings/USE_WORKFLOW_INDEX')
        const revisionMode = await apiGet('settings/' + FEATURE_GROUP_CONTROLLED_REVISIONS_SETTING).catch(() => ({}))
        setFeatureGroupRevisionControlled(isFeatureGroupRevisionControlled(revisionMode))
        const machineTimeSetting = await apiGet('settings/SHOW_TASK_MACHINE_TIME').catch(() => ({}))
        setShowMachineTime(machineTimeSetting?.value?.toLowerCase() === 'true')
        const useCascadeApproval = await apiGet('settings/WORKFLOW_CASCADE_APPROVAL')
        const branchDetail = await apiGet('settings/WORKFLOW_TREE_BRANCH_DETAIL')
        setIndexValues(indexValues)
        setWorkflowUseIndex(useIndex.value?.toLowerCase() === 'true')
        setCascadeApprovalEnabled(useCascadeApproval.value?.toLowerCase() === 'true')
        // Si la lectura falla el catch deja el estado como estaba, y arranca en true, que es el mismo
        // valor por defecto que pone la migracion: una instalacion sin la fila se comporta igual que
        // una que la tiene puesta y sin tocar.
        setDetailedBranches(branchDetail.value?.toLowerCase() === 'true')
        setProgressCalculationTypes(data)
        setResourceTypes(result)
      } catch (error) {
        console.error('Error fetching progressCalculationTypes or resourceTypes', error)
      }
    }
    if (firstLoad) {
      fetchData()
      setFirstLoad(false)
    }
  }, [firstLoad])

  const newWorkflow = () => {
    return {
      // RN01.6/RNT5 (enmienda 2 GATE 2): code is the workflow's functional identifier (was
      // "name"); name is the new, free, nullable descriptive field.
      code: '',
      name: '',
      description: '',
      version: 1,
      nodes: [],
      workflowPositions: [],
      tempUUIDMap: [],
      tempWorkflowUUID: getRandomUUID(),
      hasWorkflowResources: false,
      billOfMaterials: {
        version: '',
        indexValue: '',
        revisionBom: ''
      },
      indexValue: indexValues.find(index => index.name === 'DRAFT')
    }
  }

  const getInitialWorkflow = () => {
    if (!initialWorkflow) {
      return newWorkflow()
    }

    const wf = clearTempDocumentScopes(structuredClone(initialWorkflow))
    wf.tempWorkflowUUID = getRandomUUID()
    wf.nodes?.forEach(node => {
      if (node.jobReference?.taskReferences) {
        node.jobReference.taskReferences.sort((a, b) => a.taskOrder - b.taskOrder)
      }
    })
    return wf
  }

  /**
   * @param {TechnologyNodeData} node
   * @param {import('../../generic/utilities/objectStructures/workflow/workflow').JobReference}
   */
  const newNode = (id, node, jobReference, subWorkflow, position, sourceProcess = {}) => {
    return {
      id,
      nodeType: node.nodeType,
      // Historia 152 (S12/S15/S20/S94/S95 · `RN-01.10`, `RN-01.10b`, D10 + D12): el proceso de
      // origen del nodo — el **par** `(código, nº de versión)` de la plantilla RAÍZ de cuyo árbol se
      // arrastró—. Es el ÚNICO dato de origen que existe (D11-bis), es del NODO y no del vínculo, y
      // es inmutable desde que el nodo se crea (IN8).
      //
      // `toSourceProcessPair` es el guardián de `IN16` en el cliente: los dos campos o ninguno.
      // Medio par nace VACÍO en vez de viajar al guardado, que lo rechazaría con `E-ORG-01`. El
      // código vacío lo normaliza Sun a nulo (`blankToNull`) y la versión viaja `null`, que es lo
      // que el `Float sourceProcessVersionNumber` del DTO espera (§8.3).
      ...toSourceProcessPair(sourceProcess?.sourceProcessCode, sourceProcess?.sourceProcessVersionNumber),
      // RN01.6/RNT5: node.code is the functional identifier (was "name"); node.name is the new,
      // free, nullable descriptive field.
      code: node.nodeCode,
      name: node.nodeName,
      nodeResults: [],
      subWorkflow,
      isFirst: node.isFirst,
      jobReference,
      workflow: null,
      position,
      rule: null,
      // S26/S30: a freshly created node is never skipped.
      skipped: false,
      skipReason: '',
      skippedBy: '',
      skippedAt: ''
    }
  }

  /**
   * @param {WorkflowEditingData} oldData Old EditingData object
   * @param {WorkflowEditingDataAction} action EditingDataAction object
   */
  // Story 183 (RN6.4/D12, found in the E2E run against Sun): this reducer patches nodes IN PLACE, and
  // React may run it twice over the same base state (an eager computation at dispatch time that is then
  // discarded, or StrictMode). The first run removes the night mark from the shared node, so the second
  // one finds nothing to remove and the warning would be lost. The removal is therefore counted on that
  // SAME base state too, exactly as the node is patched: whichever run React keeps carries the count.
  const countNightMarkRemoval = (oldData) => {
    oldData.nightMarkRemovals = (oldData.nightMarkRemovals || 0) + 1
    return oldData.nightMarkRemovals
  }

  const applyWorkflowDataAction = (oldData, action) => {
    // Historia 130 (S7/S10, CA1.3/CA1.4): en la vista OPERATIONS los cuatro acordeones son LOS
    // MISMOS que editan un nodo del grafo, pero lo que editan es una versión de biblioteca, que no
    // pertenece a `workflow.nodes`. Las acciones de nodo se atienden aquí, sobre `currentNode` y
    // nada más: el workflow suspendido no se toca, que es justo lo que S10 exige.
    if (oldData.type?.code === 'OPERATIONS' && OPERATION_NODE_ACTIONS.includes(action.type)) {
      return reduceOperationNodeAction(oldData, action)
    }
    switch (action.type) {
      case 'SET_EDITING': {
        const editing = allowEdit ? action.editing : false
        if (editing) {
          markUnsavedChanges()
        }
        const newNode = [...nodes]
        newNode.forEach(n => { n.data.editing = editing })
        setNodes(newNode)
        // Leaving edit mode without saving (e.g. "No changes to save") abandons the edit just like a
        // revert does: the temp document scopes minted during it must be forgotten too, or the
        // documents added under them keep being read back on top of the saved ones.
        if (!editing) {
          clearTempDocumentScopes(oldData.workflow)
        }
        const data = { ...oldData, editing }
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(data.workflow)
        }
        onEditModeChange?.(editing)
        return data
      }
      case 'SET_WORKFLOW': {
        const editing = allowEdit ? action.editing : false
        if (editing) {
          markUnsavedChanges()
        }
        setUpdateReactFlow(true)
        const workflow = action.workflow
        // A fresh workflow snapshot (load, save, revert) starts with no temp document scope, at any
        // depth: Sun promotes the temp documents of every nested subworkflow on save, so a scope left
        // behind in one of them would be as alive as one left behind at the root.
        clearTempDocumentScopes(workflow)
        workflow.nodes?.forEach(node => {
          if (node.jobReference?.taskReferences) {
            node.jobReference.taskReferences.sort((a, b) => a.taskOrder - b.taskOrder)
          }
        })
        const copyWorkflow = structuredClone(action.workflow)
        setOriginalData(copyWorkflow)
        // HU-13/S44: a fresh workflow snapshot (load, save, revert) is the new baseline — whatever
        // a discarded or already saved import had removed no longer applies. The job highlight goes
        // with it: the nodes are rebuilt from scratch, so no node carries it anymore either.
        setRemovedWorkflowResources([])
        setHighlightedResourceUUID('')
        // Historia 130 (S55/S56/S61): la foto de cada job vinculado en el momento de cargarlo. Es
        // contra ella —y no contra el estado anterior del nodo— contra la que el cliente adelanta si
        // un cambio rompe el vínculo; un revert vuelve a pasar por aquí y la restaura.
        return {
          ...oldData,
          workflow,
          editing,
          currentNode: null,
          locked: action.locked,
          operationLinkBaselines: collectOperationLinkBaselines(workflow),
          // Story 186 (S115): a fresh snapshot (load, save, revert) is the new persisted state of every feature group.
          featureGroupBaselines: collectFeatureGroupBaselines(workflow),
          // Story 224 (adjustment 2026-10-29): a fresh snapshot has no pending template update — a save already applied them,
          // a revert or a reload discards them.
          ...NO_TEMPLATE_STATE
        }
      }
      case 'ADD_TEMPLATE_UPDATE': {
        // Story 224 (adjustment 2026-10-29, DL25-a): an accepted template update is a pending change of the workflow, written by its
        // Save (`templateUpdates` of the save body), AND the unit shows the content of the target version from now on (RN-07.22):
        // what is seen is what is saved. ROOT goes alone; a unit replaces the pending updates of the units it contains.
        const materialized = materializeTemplateUpdate(oldData.workflow, action.intent, action.target || {})
        const vanishedUnitIds = new Set(materialized.vanishedUnitIds)
        const pending = (oldData.templateUpdates || []).filter((intent) => !vanishedUnitIds.has(unitIdOf(intent)))
        // The intent keeps the target it loaded: a unit that was not in the loaded tree (it lives in a subworkflow not opened yet) shows it
        // the moment its content is loaded (`onUnitLoaded`); the body of the save only takes the two fields of the contract from it.
        const intent = { ...action.intent, target: action.target || {} }
        // HU-05 (RN-07.12): the unit starts again from the target — the baseline of its edits is the content it now shows (S196) — and the
        // decisions of the units that vanished with it go too (a unit with its own decision is never updated: RN-07.5).
        const baselines = Object.fromEntries(Object.entries(oldData.unitBaselines || {}).filter(([unitId]) => !vanishedUnitIds.has(unitId)))
        baselines[unitIdOf(intent)] = structuredClone(intent.scope === 'ROOT' ? materialized.workflow : intent.target)
        const newData = {
          ...oldData,
          templateUpdates: withTemplateUpdate(pending, intent, action.links || []),
          templateDecisions: (oldData.templateDecisions || []).filter((decision) => !vanishedUnitIds.has(unitIdOf(decision))),
          unitBaselines: baselines,
          pendingChanges: true
        }
        if (materialized.found) {
          const vanishedNodeIds = new Set(materialized.vanishedNodeIds)
          // The jobs of the replaced content leave with their library-operation baselines (G2: their pending intents go with the
          // nodes); the baselines of the target content start now (what a loaded subworkflow brings, as `ADOPT_SUBWORKFLOW`).
          newData.workflow = materialized.workflow
          newData.operationLinkBaselines = {
            ...Object.fromEntries(Object.entries(oldData.operationLinkBaselines || {}).filter(([nodeId]) => !vanishedNodeIds.has(nodeId))),
            ...collectOperationLinkBaselines(action.target)
          }
          newData.featureGroupBaselines = { ...collectFeatureGroupBaselines(action.target), ...(oldData.featureGroupBaselines || {}) }
          // The selected node is the unit itself (refreshed) or something that still exists; anything that vanished is deselected.
          const currentNodeId = oldData.currentNode?.id
          newData.currentNode = currentNodeId
            ? materialized.workflow.nodes.find((node) => node.id === currentNodeId) || (vanishedNodeIds.has(currentNodeId) ? null : oldData.currentNode)
            : oldData.currentNode
          setUpdateReactFlow(true)
          if (typeof setInitialWorkflow === 'function') {
            setInitialWorkflow(newData.workflow)
          }
        }
        return newData
      }
      case 'SET_TEMPLATE_DECISION': {
        // HU-05 (RN-07.5): the answer to "publish or unlink" writes nothing: it is a pending decision of the unit, written by the Save.
        return { ...oldData, templateDecisions: withTemplateDecision(oldData.templateDecisions || [], action.decision), pendingChanges: true }
      }
      case 'RETIRE_DELETED_UNITS': {
        // G4/H15: units deleted inside a SubworkflowDialog (accepted there) retire their updates and decisions, and those of what they held.
        const links = templateLinksRef.current
        const retire = (pending) => action.deletedNodes.reduce((kept, deletedNode) => withoutUpdatesOfDeletedNode(kept, deletedNode, links), pending)
        const updates = retire(oldData.templateUpdates || [])
        const decisions = retire(oldData.templateDecisions || [])
        const insertions = Object.fromEntries(Object.entries(oldData.insertedTemplateUnits || {}).filter(([nodeId]) => !action.deletedNodes.some((node) => node.id === nodeId)))
        return { ...oldData, templateUpdates: updates, templateDecisions: decisions, insertedTemplateUnits: insertions }
      }
      case 'SET_UNIT_BASELINE': {
        // The content a unit had when it was loaded after the workflow (a subworkflow opened for the first time): what its edits are measured against.
        if ((oldData.unitBaselines || {})[action.unitId]) {
          return oldData
        }
        return { ...oldData, unitBaselines: { ...(oldData.unitBaselines || {}), [action.unitId]: action.content } }
      }
      case 'APPLY_TEMPLATE_MATERIALIZATION': {
        // A `SubworkflowDialog` holds its own copy of the unit it shows: when the top-level editor accepts an update of that unit (or of a unit
        // inside it), the copy shows the target too, or accepting the dialog would put the old content back over the materialized one.
        const own = Boolean(action.unitNodeId) && action.intent.subworkflowNodeId === action.unitNodeId
        const workflow = own
          ? { ...oldData.workflow, nodes: action.target.nodes || [], workflowPositions: action.target.workflowPositions || [], subWorkflowIds: action.target.subWorkflowIds || [] }
          : materializeTemplateUpdate(oldData.workflow, action.intent, action.target).workflow
        if (workflow === oldData.workflow) {
          return oldData
        }
        setUpdateReactFlow(true)
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(workflow)
        }
        const currentNodeId = oldData.currentNode?.id
        return { ...oldData, workflow, currentNode: currentNodeId ? workflow.nodes.find((node) => node.id === currentNodeId) || null : oldData.currentNode }
      }
      case 'CREATE_NEW_WITH_NAME': {
        if (allowEdit) {
          markUnsavedChanges()
        }
        setUpdateReactFlow(true)
        const workflow = newWorkflow()
        workflow.code = action.workflowName
        setOriginalData(structuredClone(workflow))
        setRemovedWorkflowResources([])
        setHighlightedResourceUUID('')
        return { ...oldData, workflow, editing: !!allowEdit, currentNode: null, featureGroupBaselines: {}, ...NO_TEMPLATE_STATE }
      }
      case 'UPDATE_WORKFLOW': {
        const workflow = action.workflow
        const data = { ...oldData, workflow, pendingChanges: true }
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(data.workflow)
        }
        onEditModeChange?.(data.editing)
        return data
      }
      case 'SET_CURRENT_NODE': {
        return { ...oldData, currentNode: action.node, pendingChanges: true }
      }
      case 'UPDATE_CURRENT_NODE': {
        if (!oldData.currentNode) {
          return oldData
        }
        // Find index of current
        const index = oldData.workflow.nodes.findIndex((item) => item.id === oldData.currentNode.id)
        const newData = { ...oldData }
        const newNode = { ...action.node }
        let newNodeIndex
        if (newNode.nodeType === 'WORKFLOW') {
          newNodeIndex = newNode.subWorkflow.indexValue
        } else {
          newNodeIndex = newNode.jobReference.indexValue
        }

        if ((newNodeIndex.name === 'DRAFT' && newData.workflow.indexValue.name !== newNodeIndex.name) ||
                    (newNodeIndex.name === 'PROPOSAL' && newData.workflow.indexValue.name === 'APPROVED')) {
          newData.workflow.indexValue = newNodeIndex
        }

        newData.workflow.nodes[index] = newNode
        if (action.degradeWorkflow) {
          // Story 186 (S171): the workflow follows its children with the same rule as UPDATE_TASKS (S87)
          newData.workflow.indexValue = resolveDegradedIndexValue(
            newData.workflow.indexValue,
            collectWorkflowChildIndexValues(newData.workflow)
          )
        }
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        newData.currentNode = newNode
        return newData
      }
      case 'UPDATE_NODE_CODE': {
        // S54 (RN01.6/RNT5): the node "code" is the functional identifier (was "name"). Editing it
        // never touches the node's new free "name".
        const newData = { ...oldData }
        newData.workflow.nodes.find((item) => item.id === action.nodeId).code = action.nodeCode
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'UPDATE_NODE_NAME': {
        // S5/S54 (RN01.2/RNT5): the node's free, descriptive "name". Editing it never touches the
        // node's "code".
        const newData = { ...oldData }
        newData.workflow.nodes.find((item) => item.id === action.nodeId).name = action.nodeName
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'UPDATE_NODE_REWORK': {
        const newData = { ...oldData }
        newData.workflow.nodes.find((item) => item.id === action.nodeId).jobReference.rework = action.nodeRework
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'SET_NODE_IS_FIRST': {
        const newData = { ...oldData }
        newData.workflow.nodes.forEach((item) => {
          if (item.id === action.nodeId) {
            item.isFirst = action.isFirst
          }
        })
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'UPDATE_TASKS': {
        const index = oldData.workflow.nodes.findIndex((item) => item.id === oldData.currentNode.id)
        const newData = { ...oldData, pendingChanges: true }
        newData.currentNode.jobReference.taskReferences = action.taskReferences.map((value, index) => { return { ...value, taskOrder: index + 1 } })
        // S59/S60/S62 (HU-04, client-side preview): a featureGroup set to DRAFT/PROPOSAL degrades
        // its jobReference immediately, and the jobReference in turn degrades the workflow (same
        // rule Sun enforces server-side within the save), so the user sees the final state before
        // saving.
        const jobIndexValue = resolveDegradedIndexValue(
          newData.currentNode.jobReference.indexValue,
          collectFeatureGroupIndexValues(newData.currentNode.jobReference)
        )
        if (jobIndexValue !== newData.currentNode.jobReference.indexValue) {
          newData.currentNode.jobReference = { ...newData.currentNode.jobReference, indexValue: jobIndexValue }
        }
        const newNode = { ...newData.currentNode }
        newData.currentNode = newNode
        newData.workflow.nodes[index] = newNode
        newData.workflow.indexValue = resolveDegradedIndexValue(
          newData.workflow.indexValue,
          collectWorkflowChildIndexValues(newData.workflow)
        )
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'ADD_NODE': {
        const newData = { ...oldData }
        const node = newNode(
          action.nodeId, action.node, action.jobReference, action.subWorkflow, action.position,
          // D12: el par viaja entero en la acción; `newNode` lo normaliza (los dos o ninguno).
          { sourceProcessCode: action.sourceProcessCode, sourceProcessVersionNumber: action.sourceProcessVersionNumber }
        )
        // Historia 130 (S42): un nodo arrastrado desde la biblioteca nace VINCULADO, con intención
        // BIND, y guarda la foto del job con la que se vinculó — la referencia contra la que se
        // evalúa en cliente si un cambio posterior rompe el vínculo (C14, S55/S56).
        // HU-05 (RN-07.19, DL23): a template inserted in this session and edited before its first Save is a unit linked to the inserted version.
        if (node.nodeType === 'WORKFLOW' && action.subWorkflow?.id) {
          newData.insertedTemplateUnits = {
            ...(oldData.insertedTemplateUnits || {}),
            [node.id]: { templateCode: action.subWorkflow.code, templateVersion: action.subWorkflow.version, nodeCode: node.code }
          }
        }
        if (action.operationLink) {
          node.operationLink = action.operationLink
          // Historia 152 (S15): una copia local (`intent: 'COPY'`) nace EMBEBIDA, sin vínculo que
          // vigilar: no hay foto que tomar.
          if (action.operationLink.linked || action.operationLink.state) {
            newData.operationLinkBaselines = {
              ...(oldData.operationLinkBaselines || {}),
              [node.id]: structuredClone(action.jobReference || {})
            }
          }
        }
        // Historia 152 (S21 · impacto R5): un PROCESO ENTERO es la vía nueva por la que nodos
        // vinculados entran en el editor sin pasar por `SET_WORKFLOW` ni por el `ADD_NODE` de un
        // JOB. Sin recoger aquí su foto, editarlos después no se detectaría en cliente y la
        // cabecera seguiría diciendo "Synchronized" — un fallo SILENCIOSO y solo de UX.
        if (action.subWorkflow?.nodes) {
          newData.operationLinkBaselines = {
            ...(newData.operationLinkBaselines || oldData.operationLinkBaselines || {}),
            ...collectOperationLinkBaselines(action.subWorkflow)
          }
        }
        if (node.nodeType === 'JOB') {
          newData.workflow.indexValue = node.jobReference.indexValue
        } else {
          if ((node.subWorkflow.indexValue.name === 'DRAFT' && newData.workflow.indexValue.name !== node.subWorkflow.indexValue.name) ||
                    (node.subWorkflow.indexValue.name === 'PROPOSAL' && newData.workflow.indexValue.name === 'APPROVED')) {
            newData.workflow.indexValue = node.subWorkflow.indexValue
          }
        }
        // Already in (this reducer pushes into the SAME array, and React.StrictMode runs it twice in development):
        // a new state all the same, or React would see no change and nothing that reads the model — the census of
        // the panel, "x of y operations added" — would hear of the node until the next action (PO, 29/09/2026).
        if (newData.workflow.nodes.find((item) => item.id === node.id)) {
          return newData
        }
        if (action.subWorkflow) {
          const currentWorkflowId = oldData.workflow.id
          const candidateSubWorkflowIds = action.subWorkflow.subWorkflowIds || []
          hierarchy.registerChildWorkflow(currentWorkflowId, action.subWorkflow.id, candidateSubWorkflowIds)
        }
        newData.workflow.nodes.push(node)
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'ADD_NODE_RESULT': {
        const newData = { ...oldData }
        const node = newData.workflow.nodes.find((item) => item.id === action.nodeId)
        if (!node) {
          return oldData
        }
        // Already there: the same second run of React.StrictMode as ADD_NODE above — a new state, not the old one.
        if (node.nodeResults.find((item) => item.id === action.nodeResult.id)) {
          return newData
        }
        node.nodeResults.push(action.nodeResult)
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'DELETE_NODE_RESULT': {
        const newData = { ...oldData }
        newData.workflow.nodes.forEach((node) => {
          if (node.nodeResults) {
            node.nodeResults = node.nodeResults.filter((nr) => nr.id !== action.nodeResultId)
          }
        })

        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'DELETE_NODE': {
        const newData = { ...oldData }
        // G4 (S160): deleting a unit retires its pending template update and those of the units it contains.
        const deletedNode = newData.workflow.nodes.find((n) => n.id === action.nodeId)
        const pendingUpdates = oldData.templateUpdates || []
        const keptUpdates = withoutUpdatesOfDeletedNode(pendingUpdates, deletedNode, templateLinksRef.current)
        if (keptUpdates !== pendingUpdates) {
          newData.templateUpdates = keptUpdates
        }
        // The same for the decisions (RN-07.5, S202, S213) and for a template inserted in this session.
        const pendingDecisions = oldData.templateDecisions || []
        const keptDecisions = withoutUpdatesOfDeletedNode(pendingDecisions, deletedNode, templateLinksRef.current)
        if (keptDecisions !== pendingDecisions) {
          newData.templateDecisions = keptDecisions
        }
        if (oldData.insertedTemplateUnits?.[action.nodeId]) {
          const { [action.nodeId]: removedInsertion, ...keptInsertions } = oldData.insertedTemplateUnits
          newData.insertedTemplateUnits = keptInsertions
        }
        newData.workflow.nodes = newData.workflow.nodes.filter((n) => n.id !== action.nodeId)
        newData.workflow.workflowPositions = newData.workflow.workflowPositions?.filter((wp) => wp.nodeId !== action.nodeId)
        newData.workflow.nodes.forEach((node) => {
          if (node.nodeResults) {
            node.nodeResults = node.nodeResults.filter((nr) => nr.nextNode !== action.nodeId)
          }
        })
        handleDeleteWorkflow(oldData, action.nodeId)
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'UPDATE_JOB_REFERENCE': {
        const newData = { ...oldData }
        newData.workflow.nodes.forEach(node => {
          if (node.id === newData.currentNode.id) {
            if (node.jobReference.indexValue.name !== action.jobReference.indexValue.name) {
              if ((action.jobReference.indexValue.name === 'DRAFT' && newData.workflow.indexValue.name !== action.jobReference.indexValue.name) ||
                    (action.jobReference.indexValue.name === 'PROPOSAL' && newData.workflow.indexValue.name === 'APPROVED')) {
                newData.workflow.indexValue = action.jobReference.indexValue
              }
            }
            node.jobReference = action.jobReference
            newData.currentNode = node
          }
        })

        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'UPDATE_TYPE': {
        // Historia 130 (S10, CA1.4 / RN-01.8): entrar en OPERATIONS y volver NO es guardado ni
        // revert. El editor se SUSPENDE tal y como está —workflow, modo edición, nodo seleccionado y
        // cambios pendientes— y se restaura al volver al mismo tipo. `initialWorkflow` no se toca a
        // propósito: es la referencia contra la que se detectan esos cambios pendientes.
        if (action.editionType?.code === 'OPERATIONS') {
          return {
            ...oldData,
            type: action.editionType,
            currentNode: null,
            operationPendingChanges: false,
            suspendedEditor: oldData.suspendedEditor || {
              type: oldData.type,
              workflow: oldData.workflow,
              editing: oldData.editing,
              partReference: oldData.partReference,
              currentNode: oldData.currentNode,
              pendingChanges: oldData.pendingChanges
            }
          }
        }
        if (oldData.type?.code === 'OPERATIONS' && oldData.suspendedEditor?.type?.code === action.editionType?.code) {
          setUpdateReactFlow(true)
          return {
            ...oldData,
            ...oldData.suspendedEditor,
            suspendedEditor: null,
            operationPendingChanges: false
          }
        }
        const newData = { ...oldData, suspendedEditor: null, operationPendingChanges: false, ...NO_TEMPLATE_STATE }
        newData.type = action.editionType
        newData.workflow = newWorkflow()
        setUpdateReactFlow(true)
        newData.editing = false
        newData.partReference = null
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'UPDATE_PART_REFERENCE': {
        const newData = { ...oldData, ...NO_TEMPLATE_STATE }
        newData.partReference = action.partReference
        newData.workflow = newWorkflow()
        setUpdateReactFlow(true)
        newData.editing = false
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'SET_TEMP_UUID_FOR_NODE_ID': {
        const newData = { ...oldData }
        if (action.uuid) {
          const find = newData.workflow.tempUUIDMap.find(tempUUIDMap => tempUUIDMap.referenceUUID === action.uuid)
          if (!find) {
            newData.workflow.tempUUIDMap.push({ tempUUID: getRandomUUID(), referenceUUID: action.uuid })
          }
        }
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'UPDATE_HAS_WORKFLOW_RESOURCES': {
        const newData = { ...oldData }
        newData.workflow.hasWorkflowResources = action.hasWorkflowResources
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'UPDATE_NODE_SKIP': {
        // S26 (CA1/CA3, RN3): marks the skip intent (skipped + optional reason) directly on the
        // node, keeping node identity (same object) as UPDATE_JOB_TECHNOLOGY. The mark travels in
        // `workflowData.workflow` and is resolved by Sun on save (no dedicated endpoint).
        const node = oldData.workflow.nodes.find((item) => item.id === action.nodeId)
        if (!node) {
          return oldData
        }
        const newData = { ...oldData, pendingChanges: true }
        node.skipped = true
        node.skipReason = action.skipReason || ''
        if (oldData.currentNode?.id === node.id) {
          newData.currentNode = node
        }
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'UPDATE_NODE_REACTIVATE': {
        // S30 (CA10, RN22): clears the skip intent without asking for a reason.
        const node = oldData.workflow.nodes.find((item) => item.id === action.nodeId)
        if (!node) {
          return oldData
        }
        const newData = { ...oldData, pendingChanges: true }
        node.skipped = false
        node.skipReason = ''
        if (oldData.currentNode?.id === node.id) {
          newData.currentNode = node
        }
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'UPDATE_JOB_TECHNOLOGY': {
        const node = oldData.workflow.nodes.find((item) => item.id === action.nodeId)
        if (!node || node.nodeType !== 'JOB') {
          return oldData
        }
        const newData = { ...oldData, pendingChanges: true }
        // Story 183 (RN6.4/D12): a night job moved to a technology without night equivalent loses the
        // mark in the same act; the editor then warns (see the effect on `nightMarkRemovals`).
        const nightMark = dropNightMarkWithoutEquivalent({
          ...node.jobReference,
          technology: action.technology,
          progressCalculationTypeDTO: action.technology.progressCalculationType ?? node.jobReference.progressCalculationTypeDTO
        }, nightJobFeatureEnabled)
        node.jobReference = nightMark.jobReference
        if (nightMark.markRemoved) {
          newData.nightMarkRemovals = countNightMarkRemoval(oldData)
        }
        if (oldData.currentNode?.id === node.id) {
          newData.currentNode = node
        }
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(newData.workflow)
        }
        return newData
      }
      case 'ADOPT_SUBWORKFLOW': {
        // PO review 2026-09-11 (the Tree edits nested operations): the operations of a set that the Tree
        // loaded OUTSIDE the model (RN35) are adopted into it the moment one of them is edited — the same
        // thing `onAcceptSubworkflow` does with an edited subworkflow — so the edit travels in this
        // workflow's save, nested subworkflows included.
        const node = oldData.workflow.nodes.find((item) => item.id === action.nodeId)
        if (!node || node.nodeType !== 'WORKFLOW' || !action.subWorkflow) {
          return oldData
        }
        node.subWorkflow = action.subWorkflow
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(oldData.workflow)
        }
        // Historia 152 (S21 · impacto R5): adoptar un subworkflow es HIDRATARLO dentro del modelo
        // del editor, otra vía por la que nodos vinculados entran sin `SET_WORKFLOW`. Su foto de
        // línea base se recoge aquí, igual que la de los que entran con el proceso entero.
        return {
          ...oldData,
          pendingChanges: true,
          operationLinkBaselines: {
            ...(oldData.operationLinkBaselines || {}),
            ...collectOperationLinkBaselines(action.subWorkflow)
          },
          // Story 186: groups of a subworkflow loaded now are persisted state; what is already known keeps its baseline.
          featureGroupBaselines: {
            ...collectFeatureGroupBaselines(action.subWorkflow),
            ...(oldData.featureGroupBaselines || {})
          }
        }
      }
      case 'UPDATE_NESTED_JOB': {
        // The writer of a JOB at ANY depth, addressed by id: identity, job reference, tasks (renumbered as
        // UPDATE_TASKS does) or technology (with the same progress-calculation fallback as
        // UPDATE_JOB_TECHNOLOGY). Node identity is kept: the object is patched, never recreated.
        const node = findLevelNodes(action.nodeId, oldData.workflow.nodes).find((item) => item.id === action.nodeId)
        if (!node || node.nodeType !== 'JOB') {
          return oldData
        }
        const changes = action.changes || {}
        if (changes.name !== undefined) {
          node.name = changes.name
        }
        if (changes.code !== undefined) {
          node.code = changes.code
        }
        if (changes.jobReference) {
          node.jobReference = changes.jobReference
        }
        if (changes.taskReferences) {
          node.jobReference = { ...node.jobReference, taskReferences: changes.taskReferences.map((value, index) => ({ ...value, taskOrder: index + 1 })) }
        }
        if (changes.technology) {
          // Story 183 (RN6.4/D12): same rule as UPDATE_JOB_TECHNOLOGY, at any depth.
          const nightMark = dropNightMarkWithoutEquivalent({
            ...node.jobReference,
            technology: changes.technology,
            progressCalculationTypeDTO: changes.technology.progressCalculationType ?? node.jobReference.progressCalculationTypeDTO
          }, nightJobFeatureEnabled)
          node.jobReference = nightMark.jobReference
          if (nightMark.markRemoved) {
            countNightMarkRemoval(oldData)
          }
        }
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(oldData.workflow)
        }
        return { ...oldData, pendingChanges: true }
      }
      case 'INSERT_NODES_AFTER': {
        // Story 167 (D5 · RN-08.4): the plan is computed outside (`insertAfterPlan.js`) with its ids already
        // made, and applied here in one go, so Revert undoes the whole drop and Save carries the arrows like
        // any other. All or nothing: if X or one of the new nodes is not in the level, nothing changes.
        const levelNodesById = new Map(oldData.workflow.nodes.map((node) => [node.id, node]))
        const afterNode = levelNodesById.get(action.plan.afterNodeId)
        const insertedNodeIds = Object.keys(action.plan.resultsByNodeId)
        if (!afterNode || insertedNodeIds.some((nodeId) => !levelNodesById.has(nodeId))) {
          return oldData
        }
        afterNode.nodeResults = action.plan.afterNodeResults
        insertedNodeIds.forEach((nodeId) => {
          levelNodesById.get(nodeId).nodeResults = action.plan.resultsByNodeId[nodeId]
        })
        if (typeof setInitialWorkflow === 'function') {
          setInitialWorkflow(oldData.workflow)
        }
        return { ...oldData, pendingChanges: true }
      }
      default:
        return oldData
    }
  }
  const isProcessingDelete = useRef(false)

  const handleDeleteWorkflow = useCallback((oldData, nodeId = null) => {
    if (isProcessingDelete.current) {
      return
    }

    const nodeToDelete = nodeId
      ? oldData.workflow.nodes.find(n => n.id === nodeId)
      : oldData.currentNode

    if (nodeToDelete?.nodeType === 'WORKFLOW' && nodeToDelete?.subWorkflow) {
      isProcessingDelete.current = true

      const currentWorkflowId = oldData.workflow.id
      const deletedWorkflowId = nodeToDelete.subWorkflow.id
      const deletedSubWorkflowIds = nodeToDelete.subWorkflow.subWorkflowIds || []
      hierarchy.unregisterChildWorkflow(currentWorkflowId, deletedWorkflowId, deletedSubWorkflowIds)
      setTimeout(() => {
        isProcessingDelete.current = false
      }, 0)
    }
  }, [hierarchy])

  /** @type {[WorkflowEditingData, React.Dispatch<WorkflowEditingDataAction>]} */
  /**
   * Story #150 (S2/S5): the actions above rewrite the workflow IN PLACE — `newData.workflow.nodes[i] = ...`
   * mutates the very array `oldData.workflow` points at, and `workflow` itself is never cloned. The graph
   * does not notice because it reads `currentNode` on every render, but the tree projects its rows through
   * `useMemo` keyed on the workflow's identity, which never changes, so a task added or deleted never
   * reached the row. Bumping a revision on every action that produces a new state object gives those memos
   * something that does change, without touching the twenty-odd cases one by one.
   */
  const onWorkflowDataDispatch = (oldData, action) => {
    const newData = applyWorkflowDataAction(oldData, action)
    if (newData === oldData) {
      return newData
    }
    return { ...newData, modelRevision: (oldData.modelRevision || 0) + 1 }
  }

  // S90 (historia 170, J4a) — `initialWorkflow` (and so its `nodes`) is already fully loaded by the
  // time this component mounts (`MainWorkflows.jsx` only renders it, with a fresh `key`, once the
  // fetch resolves): the job node to select is already there on the very FIRST render, so it is seeded
  // straight into the reducer's initial state (no extra mount effect, no extra render/act cycle, and
  // no `pendingChanges: true` side effect the `SET_CURRENT_NODE` action carries — this is not the
  // user editing anything, just landing with a job already selected). Inert when
  // `initialJobReferenceId` is absent (S91 de esta historia — every other caller): `currentNode`
  // starts exactly as before (absent).
  const initialWorkflowForReducer = getInitialWorkflow()
  const initialSelectedJobNode = findJobNodeByJobReferenceId(initialWorkflowForReducer.nodes, initialJobReferenceId)
  const [workflowData, dispatchWorkflowData] = useReducer(
    onWorkflowDataDispatch,
    {
      workflow: initialWorkflowForReducer,
      editing: !!startInEditMode,
      pendingChanges: false,
      type: { code: 'TEMPLATE', name: t_('Template') },
      // Story 186 (S113): the persisted copy of the feature groups, taken from what was LOADED. The fabrication
      // order editors have no `originalData`, so it cannot be derived from there.
      featureGroupBaselines: collectFeatureGroupBaselines(initialWorkflowForReducer),
      // Story 224 (adjustment 2026-10-29): the template updates accepted and not saved yet (`templateUpdateIntent`).
      ...NO_TEMPLATE_STATE,
      ...toInitialCurrentNodeState(initialSelectedJobNode)
    }
  )

  useEffect(() => {
    // S85: el estado de los vínculos se relee cada vez que el workflow cambia de identidad o se
    // recarga (guardado, revert), que es justo cuando puede haber cambiado.
    const loadOperationLinks = async () => {
      if (!workflowData.workflow?.id || !isUserAuthorized('OPERATION_LIBRARY')) {
        setOperationLinks({})
        return
      }
      try {
        const links = await getWorkflowOperationLinks(workflowData.workflow.id)
        setOperationLinks(Object.fromEntries((links || []).map((link) => [link.nodeId, link])))
      } catch (error) {
        setOperationLinks({})
      }
    }
    loadOperationLinks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowData.workflow?.id, operationReloadKey])

  // Story 224 (S103-S106, CU5): the template links of the open workflow (E1). A failure reads as "no links".
  // Adjustment 2026-10-29: read again after every successful save (`templateLinksReloadKey`), also an in-place one that keeps
  // the id — the template updates it carried changed the links.
  const isNestedTemplateView = Array.isArray(inheritedTemplateLinks.links)
  const [templateLinksReloadKey, setTemplateLinksReloadKey] = useState(0)
  const ownTemplateLinks = useWorkflowTemplateLinks(isNestedTemplateView ? 0 : workflowData.workflow?.id, templateLinksReloadKey)
  const templateLinks = isNestedTemplateView ? inheritedTemplateLinks : ownTemplateLinks
  templateLinksRef.current = templateLinks.links || []
  const templateUpdateIds = useMemo(() => templateUpdateNodeIds(templateLinks.links), [templateLinks.links])
  // The notice above the canvas speaks only for the workflow being viewed: the ROOT link here, the viewed subworkflow's link inside
  // a `SubworkflowDialog`. Its children's notices are in the General Properties of their node.
  const viewedTemplateLinks = isNestedTemplateView && !inheritedTemplateLinks.unitNodeId
    ? []
    : viewedUnitLinks(templateLinks.links, isNestedTemplateView ? inheritedTemplateLinks.unitNodeId : '')

  // S113/S125/S129: a rejection of E2 or E3. The 409 of the series (#161) opens today's version-conflict dialog, which offers to
  // reload the vigent version (nothing else is opened until the user chooses). Any other Sun literal is the i18n key of an alert,
  // and the banner is re-read so it reflects what Sun says now. A save carrying template updates reports through the save's path.
  const handleTemplateLinkError = async (thrown) => {
    const conflict = readWorkflowVersionConflict(thrown)
    if (conflict.isConflict) {
      setWorkflowVersionConflict({ open: true, latestWorkflowId: conflict.latestWorkflowId, latestVersion: conflict.latestVersion })
      return
    }
    showAlert({ title: translateTemplateLinkError(t_, thrown), severity: 'error' })
    await templateLinks.reload()
  }

  // S119 (CU8): Update of a banner row asks Sun what the update would do (E3, read-only) toward the version the banner shows.
  // "View changes" of a pending row shows the impact that was accepted again, view-only. `impact: {}` keeps the dialog closed.
  const [templateImpactDialog, setTemplateImpactDialog] = useState(NO_TEMPLATE_IMPACT_DIALOG)
  const pendingTemplateUpdates = workflowData.templateUpdates || []
  const openTemplateUpdate = async (link) => {
    try {
      const impact = await getTemplateUpdateImpact(workflowData.workflow.id, link.subworkflowNodeId, link.latestApprovedTemplateWorkflowId)
      // G2 (S158, S161): the jobs of the unit with a pending library-operation intent; accepting replaces them, so the dialog warns.
      setTemplateImpactDialog({ impact: impact || {}, link, viewOnly: false, discardedOperationJobs: discardedOperationJobCodes(workflowData.workflow, link) })
    } catch (thrown) {
      await handleTemplateLinkError(thrown)
    }
  }
  const viewPendingTemplateUpdate = (link) => {
    const intent = pendingUpdateOf(pendingTemplateUpdates, link)
    if (intent.scope) {
      setTemplateImpactDialog({ impact: intent.impact, link, viewOnly: true, discardedOperationJobs: [] })
    }
  }

  // S127 (adjustment 2026-10-29): confirming in the impact dialog WRITES NOTHING, like an operation-library update
  // (`updateNodeOperation`): the update becomes a pending change of THIS (top-level) editor — also when it was asked from inside a
  // `SubworkflowDialog`, whose actions are these — and the window's Save sends it in `templateUpdates`, as a new version or in place.
  // DL25-a (RN-07.22): and the unit shows the content of the target version from now on, loaded with `GET workflow/{id}` as inserting
  // it would load it; if that load fails nothing is left pending. Revert discards it with everything else (it reloads the saved copy).
  // Update (like Dismiss) is only enabled in edit mode (adjustment 2026-10-29).
  const materializingTemplateUpdate = useRef(false)
  const [templateMaterialization, setTemplateMaterialization] = useState({ seq: 0, intent: {}, target: {} })
  const confirmTemplateUpdate = async () => {
    const { impact, link } = templateImpactDialog
    if (materializingTemplateUpdate.current) {
      return
    }
    materializingTemplateUpdate.current = true
    let target
    try {
      target = await getTemplateTargetWorkflow(impact.toTemplateWorkflowId)
      if (!target?.id || target.error || !Array.isArray(target.nodes)) {
        throw new Error('Error loading data')
      }
      target.tempWorkflowUUID = getRandomUUID()
      clearTempDocumentScopes(target)
    } catch (thrown) {
      showAlert({ title: t_('Error loading data'), severity: 'error', message: t_(thrown?.message || 'Error loading data') })
      return
    } finally {
      materializingTemplateUpdate.current = false
    }
    markUnsavedChanges()
    const intent = templateUpdateIntent(link, impact)
    dispatchWorkflowData({ type: 'ADD_TEMPLATE_UPDATE', intent, links: templateLinks.links, target })
    // A `SubworkflowDialog` that is open holds its own copy of the unit: it is told, so accepting it does not undo this (gap of phase A).
    setTemplateMaterialization((previous) => ({ seq: previous.seq + 1, intent, target }))
    setTemplateImpactDialog(NO_TEMPLATE_IMPACT_DIALOG)
    showAlert({ title: t_('Template update pending: save the workflow to apply it'), severity: 'info' })
  }

  // S112 (CU7): Dismiss of a banner row. Nothing is saved with the workflow, so the editor is not marked as modified.
  const dismissTemplateUpdate = async (link) => {
    try {
      await dismissTemplateLink(workflowData.workflow.id, {
        subworkflowNodeId: link.subworkflowNodeId,
        templateVersion: link.latestApprovedTemplateVersion
      })
    } catch (thrown) {
      await handleTemplateLinkError(thrown)
      return
    }
    await templateLinks.reload()
  }
  // What each notice row knows about its unit: its pending update, if any. A unit that a pending update replaces is no longer in the
  // editor (DL25-a), so nothing is ever blocked by another pending update, and unsaved changes of any kind do not block it either.
  const pendingTemplateDecisions = workflowData.templateDecisions || []
  const templateUnitStateOf = (link) => ({
    pending: pendingUpdateOf(pendingTemplateUpdates, link),
    // HU-05 (RN-07.5): a unit with a decision offers neither Update nor Dismiss.
    decision: pendingUpdateOf(pendingTemplateDecisions, link)
  })

  // ---- HU-05: editing a linked unit asks "publish or unlink" (DL14, DL15; RN-07.1-RN-07.5) ----------------------------------------------
  // The question belongs to the top-level editor in the last version of the workflow, for who can save workflows (RN-07.20). A
  // `SubworkflowDialog` asks through the actions it borrows from it.
  const questionsEnabled = !isNestedTemplateView && !!allowEdit && isUserAuthorized('WORKFLOWS_TAB') && templateLinks.latestVersion === true && !!workflowData.editing
  // Every linked unit the editor knows, the ROOT first: those of E1 and the templates inserted in this session (RN-07.19).
  const templateUnits = () => {
    const links = templateLinks.links || []
    const subworkflows = links.filter((link) => link.scope === 'SUBWORKFLOW')
    const inserted = Object.entries(workflowData.insertedTemplateUnits || {})
      .filter(([nodeId]) => !subworkflows.some((link) => link.subworkflowNodeId === nodeId))
      .map(([nodeId, info]) => ({
        scope: 'SUBWORKFLOW',
        subworkflowNodeId: nodeId,
        nodePath: [info.nodeCode || info.templateCode],
        templateCode: info.templateCode,
        templateVersion: info.templateVersion,
        parentUnitNodeId: '',
        topLevelNodeId: nodeId,
        latestTemplateVersion: 0
      }))
    return [...(rootLinkOf(links).templateCode ? [rootLinkOf(links)] : []), ...subworkflows, ...inserted]
  }
  const linkedUnitIds = new Set(templateUnits().filter((unit) => unit.scope === 'SUBWORKFLOW').map((unit) => unit.subworkflowNodeId))
  // What a unit's edits are measured against: its last materialization or lazy load, else the workflow as it was loaded (DL14).
  const unitBaselineOf = (unitId) => (workflowData.unitBaselines || {})[unitId] ||
    (unitId === '' ? originalData : findNodeDeep((originalData || {}).nodes || [], unitId).subWorkflow) || {}
  const decisionRowOf = (unit) => {
    const pending = pendingUpdateOf(pendingTemplateUpdates, unit)
    return {
      unitId: unitIdOf(unit),
      isRoot: unit.scope === 'ROOT',
      where: unit.scope === 'ROOT' ? t_('Whole workflow') : (unit.nodePath || []).join(' / '),
      templateCode: unit.templateCode,
      templateVersion: unit.templateVersion,
      pendingTargetVersion: pending.scope ? Number(pending.targetTemplateVersion) : 0,
      newerVersion: newerTemplateVersionOf(unit, publishBaseVersionOf(unit, pending)),
      link: unit
    }
  }
  const [decisionDialog, setDecisionDialog] = useState(NO_DECISION_DIALOG)
  const decisionResolver = useRef(() => {})
  /** Opens the question; resolves `true` once every row has a decision, `false` if the user cancels (what was decided stays decided). */
  const askTemplateDecisions = (rows, single) => new Promise((resolve) => {
    decisionResolver.current = resolve
    setDecisionDialog({ rows, single })
  })
  const decideTemplateUnit = (row, action, indexValueName) => {
    markUnsavedChanges()
    dispatchWorkflowData({ type: 'SET_TEMPLATE_DECISION', decision: templateDecision(row.link, action, indexValueName) })
    const rows = decisionDialog.rows.filter((other) => other.unitId !== row.unitId)
    setDecisionDialog(rows.length > 0 ? { ...decisionDialog, rows } : NO_DECISION_DIALOG)
    if (rows.length === 0) {
      decisionResolver.current(true)
    }
  }
  const cancelTemplateDecisions = () => {
    setDecisionDialog(NO_DECISION_DIALOG)
    decisionResolver.current(false)
  }
  const isDecided = (unit) => Boolean(pendingUpdateOf(pendingTemplateDecisions, unit).scope)
  /** DL15: the `SubworkflowDialog` of a linked unit is being accepted: its edits (those of the unit, not of the units nested in it) are asked now. */
  const reviewAcceptedUnit = async (unitId, content) => {
    const unit = templateUnits().find((candidate) => candidate.scope === 'SUBWORKFLOW' && candidate.subworkflowNodeId === unitId)
    if (!questionsEnabled || !unit || isDecided(unit) || !isUnitEdited({ current: content, baseline: unitBaselineOf(unitId), linkedIds: linkedUnitIds })) {
      return true
    }
    return askTemplateDecisions([decisionRowOf(unit)], true)
  }
  /** DL15: on Save, one question for every edited unit without a decision, the ROOT first; the Save does not go on until all are decided. */
  const reviewUndecidedUnits = async () => {
    if (!questionsEnabled) {
      return true
    }
    const rows = templateUnits()
      .filter((unit) => !isDecided(unit) && isUnitEdited({
        current: unitContentOf(workflowData.workflow, unitIdOf(unit)),
        baseline: unitBaselineOf(unitIdOf(unit)),
        isRoot: unit.scope === 'ROOT',
        linkedIds: linkedUnitIds
      }))
      .map(decisionRowOf)
    return rows.length === 0 ? true : askTemplateDecisions(rows, false)
  }
  /**
   * A subworkflow was just loaded (`GET workflow/{id}`) for the unit `unitId`: what it was when loaded becomes the baseline of its edits, and the
   * updates already accepted for it (or for units inside it) show their target, as if it had been loaded before they were accepted.
   */
  const loadedUnitContent = (unitId, loaded) => {
    if (!Array.isArray(unitBaselineOf(unitId).nodes)) {
      dispatchWorkflowData({ type: 'SET_UNIT_BASELINE', unitId, content: structuredClone(loaded) })
    }
    return withAcceptedUpdates(unitId, loaded)
  }
  /**
   * The PURE part of `loadedUnitContent` (no dispatch, safe to call while rendering): a subworkflow loaded outside the model — by the unit's
   * dialog or by the Process Tree, which hydrates the unopened ones on its own — shows the target of the updates already accepted for it.
   */
  const acceptedTemplateUpdates = workflowData.templateUpdates
  const withAcceptedUpdates = useCallback((unitId, loaded) => {
    const pending = acceptedTemplateUpdates || []
    const own = pending.find((intent) => unitIdOf(intent) === unitId && intent.scope === 'SUBWORKFLOW' && intent.target?.nodes)
    let content = own ? { ...loaded, nodes: own.target.nodes, workflowPositions: own.target.workflowPositions || [], subWorkflowIds: own.target.subWorkflowIds || [] } : loaded
    pending
      .filter((intent) => intent.scope === 'SUBWORKFLOW' && unitIdOf(intent) !== unitId && intent.target?.nodes)
      .forEach((intent) => { content = materializeTemplateUpdate(content, intent, intent.target).workflow })
    return content
  }, [acceptedTemplateUpdates])
  // Update/Dismiss/View changes of a notice always act on the TOP-LEVEL workflow (E2/E3 take its id, its Save writes the update),
  // so a `SubworkflowDialog` borrows them together with the state of each unit.
  const templateActions = isNestedTemplateView
    ? {
        canAct: false,
        editing: false,
        unitStateOf: () => NO_UNIT_STATE,
        onUpdate: () => {},
        onDismiss: () => {},
        onViewPending: () => {},
        onAcceptUnit: async () => true,
        onUnitLoaded: (unitId, loaded) => loaded,
        onUnitsDeleted: () => {},
        ...inheritedTemplateLinks.actions
      }
    : {
        canAct: !!allowEdit && isUserAuthorized('WORKFLOWS_TAB'),
        // Adjustment 2026-10-29: outside edit mode Update and Dismiss are shown disabled; a SubworkflowDialog reads this (top-level) one.
        editing: !!workflowData.editing,
        unitStateOf: templateUnitStateOf,
        onUpdate: openTemplateUpdate,
        onDismiss: dismissTemplateUpdate,
        onViewPending: viewPendingTemplateUpdate,
        onAcceptUnit: reviewAcceptedUnit,
        onUnitLoaded: loadedUnitContent,
        onUnitsDeleted: (deletedNodes) => dispatchWorkflowData({ type: 'RETIRE_DELETED_UNITS', deletedNodes })
      }
  const appliedMaterializationSeq = useRef(inheritedTemplateLinks.materialization?.seq || 0)
  useEffect(() => {
    const materialization = inheritedTemplateLinks.materialization
    if (!isNestedTemplateView || !materialization?.seq || materialization.seq === appliedMaterializationSeq.current) {
      return
    }
    appliedMaterializationSeq.current = materialization.seq
    dispatchWorkflowData({ type: 'APPLY_TEMPLATE_MATERIALIZATION', intent: materialization.intent, target: materialization.target, unitNodeId: inheritedTemplateLinks.unitNodeId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNestedTemplateView, inheritedTemplateLinks.materialization?.seq])
  // S111: the notice mark is patched onto the rendered nodes (no rebuild, no `fitView`) and is not a change of the workflow.
  useEffect(() => {
    if (!Array.isArray(nodes)) {
      return
    }
    const painted = applyTemplateUpdateMark(nodes, templateUpdateIds)
    if (painted !== nodes) {
      setNodes(painted)
    }
  }, [nodes, templateUpdateIds, setNodes])

  const pingEditWorkflow = useSmartInterval(() => { if (workflowData.workflow?.id) { apiGet('workflows/ping/' + workflowData.workflow?.id) } }, 5000, () => {}, false)

  // RN3/S1: opening another process is another lens — back to the graph, and `key={workflowKey}`
  // below throws away everything the previous Tree session held (folding, query, Material, cache).
  // #215 (D4): the height goes back to 50vh at the same moments, and at no other.
  const workflowKey = workflowData.workflow?.id || workflowData.workflow?.tempWorkflowUUID
  useEffect(() => {
    setSurface('graph')
    setSurfaceHeight(SURFACE_INITIAL_HEIGHT)
  }, [workflowKey])

  useEffect(() => {
    pingEditWorkflow(workflowData.editing)
  }, [pingEditWorkflow, workflowData.editing])

  const handleOpenSaveDialog = async () => {
    try {
      // RN01.6/RNT5: the required identifier is the workflow "code" (was "name")
      if (workflowData.workflow.code.trim() === '') {
        showAlert({ title: t_('Code is required'), severity: 'warning', message: '' })
        return
      }
      // Check if there are pending documents
      // Update/Create workflowPositions based on nodes positions
      workflowData.workflow.workflowPositions = updatePositions(nodes, workflowData.workflow)
      // Compare original data with current data
      const diff = await diffWorkflowWithWorkflowResources(originalData, workflowData.workflow)
      // `document/checkTemp` intersects the received list with the keys of Sun's temp document map,
      // which are the TEMP UUIDs (sending the reference UUIDs never matched, so an edit whose only
      // change was a document was reported as "No changes to save" and never saved). Nested
      // subworkflows count too: their temp documents are promoted by the same save.
      const existsPendingDocuments = await apiPut('document/checkTemp', collectTempDocumentUUIDs(workflowData.workflow))
      // Story 224 (adjustment 2026-10-29): an accepted template update is a change to save even when nothing else changed.
      if (diff.equals && !existsPendingDocuments.value && (workflowData.templateUpdates || []).length === 0 && (workflowData.templateDecisions || []).length === 0) {
        // No changes
        showAlert({ title: t_('No changes to save'), severity: 'info', message: '' })
        clearUnsavedChanges()
        dispatchWorkflowData({ type: 'SET_EDITING', editing: false })
        return
      }
      // HU-05 (DL15): before anything is saved every edited linked unit without a decision is asked about; Cancel saves nothing.
      if (!(await reviewUndecidedUnits())) {
        return
      }
      setDiff(diff)
      // S86 (RN-05.6 / contrato-sun.md §7 deviation 9): on a workflow LINKED to a library BOM, a
      // temp load present at save time makes Sun create version n+1 of that BOM and re-point the
      // workflow at it. That is a change to shared master data, so it is announced BEFORE the save
      // runs — the user can still back out. Everything else about the save is untouched.
      if (shouldWarnNewVersionOnSave({
        link: bomLink,
        hasTempLoad: Boolean(workflowData.workflow?.hasWorkflowResources),
        canEdit: isUserAuthorized('BOM_LIBRARY_EDIT'),
        // S71 (corrección PO 2026-09-09): the structure on screen is the one of the version about
        // to be LINKED, not an edit of the currently linked BOM — that save links, it does not
        // create a version of the BOM the workflow is leaving behind.
        hasPendingLink: Boolean(workflowData.workflow?.pendingBomLink?.bomId)
      })) {
        setBomNewVersionWarning(true)
        return
      }
      setSaveDialogOpen(true)
    } catch (error) {
      showAlert({ title: t_('Error preparing save dialog:'), severity: 'error', message: t_(error.message) })
    }
  }

  const diffWorkflowWithWorkflowResources = async (originalWorkflow, currentWorkflow) => {
    const originalWorkflowForDiff = isObjectForDiff(originalWorkflow) ? structuredClone(originalWorkflow) : {}
    const currentWorkflowForDiff = isObjectForDiff(currentWorkflow) ? structuredClone(currentWorkflow) : {}
    const tempWorkflowResources = currentWorkflow?.tempWorkflowUUID
      ? await apiGet('workflowResource/' + currentWorkflow.tempWorkflowUUID)
      : []

    if (tempWorkflowResources.length > 0) {
      originalWorkflowForDiff.workflowResources = currentWorkflow?.id
        ? await apiGet('workflowResource/' + getRandomUUID() + '/' + currentWorkflow.id)
        : []
      currentWorkflowForDiff.workflowResources = tempWorkflowResources
    }

    return diffObjects((originalWorkflowForDiff), (currentWorkflowForDiff),
      ['workflowPositions', 'workflow']
    )
  }

  /**
   * S71 (HU-05/CA5.1, RN-05.2): writes the link the user chose while editing (N16). The discard of
   * an embedded BOM or of a previous link was already confirmed in the header, which is why
   * `confirmDiscardEmbedded` travels with the choice; if Sun still answers with one of its two
   * replacement literals — the workflow's BOM origin may have changed since the choice was made —
   * the request is retried once confirming it, and never asked twice.
   *
   * S135 (corrección PO 2026-09-10): with `mode: 'UPDATE'` the pending change is a move to a newer
   * version of the BOM the workflow is ALREADY linked to, which is a different operation (N20, and
   * it is Sun who writes its `WorkflowChangeLog` entry — contrato-sun.md §7 deviation 6). Both
   * modes are resolved here because both are "the pending BOM decision this save has to write
   * before the workflow itself".
   *
   * @param {number} workflowId
   * @param {object} pendingBomLink `{mode, bomId, confirmDiscardEmbedded}` (plus the labels the
   * header shows)
   * @returns {Promise<{linked: boolean, error: string, retiredAssignments: object[]}>}
   */
  const applyPendingBomLink = async (workflowId, pendingBomLink) => {
    if (pendingBomLink.mode === 'UPDATE') {
      try {
        const result = await updateWorkflowBom(workflowId, Number(pendingBomLink.bomId))
        return { linked: true, error: '', retiredAssignments: result?.retiredAssignments || [] }
      } catch (thrown) {
        return { linked: false, error: readBomLibraryError(thrown).error || 'Not allowed', retiredAssignments: [] }
      }
    }
    const body = { bomId: Number(pendingBomLink.bomId), confirmDiscardEmbedded: Boolean(pendingBomLink.confirmDiscardEmbedded) }
    try {
      await linkWorkflowBom(workflowId, body)
      return { linked: true, error: '', retiredAssignments: [] }
    } catch (thrown) {
      const { status, error } = readBomLibraryError(thrown)
      if (status === 409 && BOM_LINK_REPLACEMENT_LITERALS.includes(error) && !body.confirmDiscardEmbedded) {
        try {
          await linkWorkflowBom(workflowId, { ...body, confirmDiscardEmbedded: true })
          return { linked: true, error: '', retiredAssignments: [] }
        } catch (retried) {
          return { linked: false, error: readBomLibraryError(retried).error || 'Not allowed', retiredAssignments: [] }
        }
      }
      return { linked: false, error: error || 'Not allowed', retiredAssignments: [] }
    }
  }

  /**
   * S135 (RN-07.6): what N20 could not keep. The assignments it retired are named — by the code of
   * the component that disappeared — instead of quietly vanishing from the workflow.
   *
   * @param {object[]} retiredAssignments `ImpactedComponent[]` of the `BomUpdateResult`
   */
  const reportRetiredAssignments = (retiredAssignments = []) => {
    if (retiredAssignments.length === 0) {
      return
    }
    showAlert({
      severity: 'warning',
      title: t_('Retired assignments'),
      message: retiredAssignments.map((retired) => retired?.component?.resourceCode).filter(Boolean).join(', ')
    })
  }

  const saveWorkflow = async (overwriteData, propagate, comment, currentSelectedIndexValue = 'APPROVED', applyCascade = false, cascadeWorkflow = null) => {
    // Historia 161 (RN18/R-7): released in `finally` below, so EVERY exit of this function —
    // success or any error, including the early `return`s below — leaves the buttons free again.
    // `beginSave()` already reserved the save at the click that led here (SAVE, RN15/RN16); this is
    // where the screen lock starts, once every dialog derived from SAVE has been accepted.
    savingRef.current = true
    setIsSaving(true)
    try {
      const validation = validateWorkflow(workflowData.workflow, nodes, t_, frontendConfig)
      if (!validation.isValid) {
        showAlert({ title: validation.message, severity: 'error' })
        return
      }
      let result
      // S16/S17 (parte jupiter) + enmienda PO 23/07/2026: the cascade adjusts every child
      // indexValue travelling inside the same save payload to the SELECTED target (any value, not
      // only APPROVED); the atomicity of the whole cascade is guaranteed by the transaction that
      // already wraps this save server-side (sun M5-correction), no dedicated endpoint involved.
      // `cascadeWorkflow` is the fully loaded tree (nested subworkflows included) hydrated by the
      // save dialog before listing the pending items, so the cascade reaches every level.
      // S71 (corrección PO 2026-09-09): `pendingBomLink` is Jupiter's own editing state — the
      // library BOM version the user chose while editing — and Sun does not know the field, so it
      // is stripped from the payload and applied through N16 by this very save.
      const { pendingBomLink, ...cascadedWorkflow } = applyCascade ? applyCascadeApproval(cascadeWorkflow || workflowData.workflow, indexValues, currentSelectedIndexValue, { controlled: featureGroupRevisionControlled }) : workflowData.workflow
      // Story 183 (C1, RN8.1): the night mark goes explicit on every job, or Sun would keep the persisted one.
      // Story 186 (S85, CU4-A3): in CONTROLLED mode a group undone until identical goes back as APPROVED vN, and no
      // client seals travel; FREE mode sends the workflow untouched.
      const workflowToSave = withExplicitNightJob(prepareFeatureGroupsForSave(cascadedWorkflow, workflowData.featureGroupBaselines, { controlled: featureGroupRevisionControlled }))
      // ¿Viaja alguna intención de biblioteca en este guardado? De ello depende releer el workflow
      // después: el contenido del job vinculado lo escribe el servidor, no este payload.
      const hadOperationIntent = (workflowToSave?.nodes || []).some(
        (node) => node?.operationLink?.intent && node.operationLink.intent !== 'KEEP'
      )
      // Story 224 (adjustment 2026-10-29): the template updates the user accepted travel in the body of THIS save, and Sun applies
      // them in its transaction, as a new version or in place. Omitted when there is none (Sun reads both as "none").
      const templateUpdates = toTemplateUpdatesBody(workflowData.templateUpdates || [])
      // HU-05 (RN-07.5): the decisions travel in the same save. The ROOT one says whether the BOM or the documents were touched after its
      // update was accepted (DL26): only then the workflow keeps its own instead of the template's.
      const rootUpdateIsPending = (workflowData.templateUpdates || []).some((intent) => intent.scope === 'ROOT')
      const templateLinkChanges = toTemplateLinkChangesBody(workflowData.templateDecisions || [], {
        rootBomAndDocumentsFromEditor: rootUpdateIsPending && rootBomAndDocumentsEdited(workflowData.workflow, unitBaselineOf(''))
      })
      const savedBody = {
        ...workflowToSave,
        ...(templateUpdates.length > 0 ? { templateUpdates } : {}),
        ...(templateLinkChanges.length > 0 ? { templateLinkChanges } : {})
      }
      if (workflowData.workflow.id) {
        // The link must exist BEFORE the workflow is written: Sun's save reads the workflow's BOM
        // origin to decide what to do with the structure. If linking is rejected nothing is saved —
        // the user keeps every change on screen and can retry or pick another version.
        if (pendingBomLink?.bomId) {
          const linkResult = await applyPendingBomLink(workflowData.workflow.id, pendingBomLink)
          // Enmienda 2 (H1/S74, RN36): the editor closed while this pre-`PUT` phase was in flight —
          // the chain is abandoned here, with no `PUT` and no explicit `unLock` (the edit
          // reservation just expires on its own once the ping stops, like DL8's BOM link already
          // does today).
          if (!mountedRef.current) {
            return
          }
          if (!linkResult.linked) {
            showAlert({
              severity: 'error',
              title: t_(pendingBomLink.mode === 'UPDATE' ? 'Update to a newer BOM version' : 'Link library BOM'),
              message: t_(linkResult.error)
            })
            return
          }
          reportRetiredAssignments(linkResult.retiredAssignments)
        }
        if (workflowData.partReference) {
          result = await apiPut('workflow/' + !overwriteData + '/' + propagate + '/' + currentSelectedIndexValue + '/' + workflowData.partReference.id, savedBody)
        } else {
          result = await apiPut('workflow/' + !overwriteData + '/false/' + currentSelectedIndexValue, savedBody)
        }
      } else {
        if (workflowData.partReference) {
          result = await apiPost('workflow/' + workflowData.partReference.id, workflowToSave)
        } else {
          result = await apiPost('workflow', workflowToSave)
        }
        // S71: a workflow never saved has no id to link against, so its link is written right after
        // it is created. The workflow itself IS saved by then: a failure here is a warning, not a
        // lost save, and the user can link again from the header.
        // S135: a workflow that was never saved cannot be UPDATING a link it does not have yet —
        // the update is only ever offered over an existing link. Should it happen, it is a bug, not
        // a link to write: the workflow is saved and the impossible operation reported.
        if (pendingBomLink?.mode === 'UPDATE') {
          showAlert({ severity: 'error', title: t_('Update to a newer BOM version'), message: t_('Workflow is not linked to a library BOM') })
        } else if (pendingBomLink?.bomId) {
          const linkResult = await applyPendingBomLink(result.workflow.id, pendingBomLink)
          if (!linkResult.linked) {
            showAlert({ severity: 'warning', title: t_('Link library BOM'), message: t_(linkResult.error) })
          }
        }
      }
      // Enmienda 2 (RN35): the `PUT`/`POST` already reached the server — leaving the editor from
      // here on no longer abandons anything, it only stops the UI from reacting to what comes back.
      if (mountedRef.current) {
        showAlert({ title: t_('Saved correctly'), severity: 'success', message: '' })
        updateIdValues(result)
      }
      // Add needed IDs to diff
      diff.newDto = addIdsToDiff(diff.newDto, result.workflow)

      diff.newDto = { ...diff.newDto, id: result.workflow.id }
      // Enmienda 2 (RN35): the change log entry is a server step — it is written the same whether
      // the editor is still open or not.
      if (workflowData.partReference) {
        await apiPost('workflowChangeLog/' + workflowData.partReference.id, { ...diff, comment }).catch(() => {
          if (mountedRef.current) {
            showAlert({ title: t_('Error saving change log'), severity: 'warning', message: '' })
          }
        })
      } else {
        await apiPost('workflowChangeLog', { ...diff, comment }).catch(() => {
          if (mountedRef.current) {
            showAlert({ title: t_('Error saving change log'), severity: 'warning', message: '' })
          }
        })
      }

      // Reload data
      if (!overwriteData) {
        result.workflow.tempWorkflowUUID = getRandomUUID()
      }
      if (!result.workflow.tempWorkflowUUID) {
        result.workflow.tempWorkflowUUID = workflowData.workflow.tempWorkflowUUID || getRandomUUID()
      }
      // Enmienda 2 (RN35/RN36): the edit reservation is released on success either way — with the
      // editor closed there is no Revert/Recargar left to do it later.
      if (workflowData.workflow.id) {
        apiGet('workflows/unLock/' + workflowData.workflow.id)
      }
      // Enmienda 2 (CA25/RN35): everything below is interface — repainting the editor, the global
      // unsaved-changes indicator and the shared node state of the Workflows screen, that a closed
      // editor must not touch (they belong to whatever screen the user is on now).
      if (!mountedRef.current) {
        return
      }
      clearUnsavedChanges()
      // Historia 130: cuando alguna intención de biblioteca viajó en el guardado, el workflow se
      // relee entero antes de repintarlo. El contenido del job vinculado lo COPIA el servidor
      // después de persistir el nodo (RN-03.4/RN-05.9), así que la respuesta del guardado todavía
      // trae lo que mandó el cliente: sin esta relectura el usuario no veía la versión nueva hasta
      // volver a cargar el workflow a mano.
      // Story 224 (adjustment 2026-10-29): same for a template update — Sun rewrites the updated units inside the save, so what
      // its response echoes may still be what the client sent.
      let savedWorkflow = result.workflow
      if ((hadOperationIntent || templateUpdates.length > 0 || templateLinkChanges.length > 0) && savedWorkflow?.id) {
        const reloaded = await apiGet('workflow/' + savedWorkflow.id)
        // Solo se acepta si de verdad vuelve un workflow: cualquier otra cosa dejaría el editor
        // pintando algo que no lo es, peor que no refrescar.
        if (reloaded?.id && !reloaded.error) {
          reloaded.tempWorkflowUUID = savedWorkflow.tempWorkflowUUID
          savedWorkflow = reloaded
        }
      }
      if (!mountedRef.current) {
        return
      }
      dispatchWorkflowData({ type: 'SET_WORKFLOW', workflow: savedWorkflow, editing: false })
      // El guardado es quien resuelve los vínculos (BIND/PUBLISH/UPDATE son intenciones, y la
      // decisión es de Sun). Al sobrescribir, el workflow conserva su id, así que los vínculos que
      // este editor tiene en memoria se quedarían como estaban: se releen (N13).
      setOperationReloadKey((key) => key + 1)
      // Story 224 (adjustment 2026-10-29): the same for the template links (E1) — the pending updates are gone with SET_WORKFLOW
      // and the notices must say what Sun says now, also after an in-place save that keeps the id.
      setTemplateLinksReloadKey((key) => key + 1)
    } catch (error) {
      // Enmienda 2 (RN36/E2-DL1): by the time the `catch` runs, the `PUT`/`POST` was already sent
      // (the only earlier exit, the pre-`PUT` BOM link, `return`s and never throws) — so an editor
      // that closed in the meantime left no one behind to Reload/Stay (RN20) or read any other
      // error. Nothing is shown, and the edit reservation of the STARTING workflow is released
      // explicitly here, unlike RN20's "no se libera" (which only holds while the editor is open).
      if (!mountedRef.current) {
        if (workflowData.workflow.id) {
          apiGet('workflows/unLock/' + workflowData.workflow.id)
        }
        return
      }
      // Historia 161 (CU6/D3/RN19/RN20/V4): Sun rejects a "save as new version" whose starting
      // version is no longer the vigent one — double click, stale retry, duplicated tab or another
      // user's save, the server does not distinguish (P1) — with this literal. It is NOT shown as a
      // raw error: no "Saved correctly" (that's below `return`), no `workflowChangeLog`, no id
      // reassignment, no `unLock` (RN20) — only the translated conflict dialog opens (RN19).
      const conflict = readWorkflowVersionConflict(error)
      if (conflict.isConflict) {
        setWorkflowVersionConflict({ open: true, latestWorkflowId: conflict.latestWorkflowId, latestVersion: conflict.latestVersion })
        return
      }
      // Story 224 (adjustment 2026-10-29): a save carrying template updates may be rejected with one of the story's literals
      // (`TEMPLATE_UPDATE_ROOT_NOT_ALONE`, `TEMPLATE_LINK_*`, `TEMPLATE_UPDATE_TARGET_*`), which ARE the i18n key. The editor keeps
      // every change, the pending updates included, and the notices are re-read so they say what Sun says now.
      if (isTemplateLinkError(error)) {
        showAlert({ title: translateTemplateLinkError(t_, error), severity: 'error' })
        templateLinks.reload()
        return
      }
      // S63/D20/E6: a duplicated planning group rejects the save with HTTP 409 and Sun's own
      // frozen English literal. Jupiter must show ITS OWN translated message naming the offending
      // value(s) (S65) — and, by returning here BEFORE the `dispatchWorkflowData` above, every
      // change the user made stays exactly as they left it (S63's "conserva en pantalla todos los
      // cambios") and no success confirmation is ever shown.
      showSaveError(error)
    } finally {
      // Historia 161 (RN18): released on EVERY exit — success or any error above, including the
      // version conflict — no client timeout (DL4: Sun's own lock has none either).
      endSave()
    }
  }

  /**
   *
   * @param {GeneratedWorkflow} generatedWorkflow
   */
  const updateIdValues = (generatedWorkflow) => {
    if (generatedWorkflow.nodeUUIDMap === null) {
      return
    }

    const newNodes = [...nodes]
    const newEdges = [...edges]
    for (const key in generatedWorkflow.nodeUUIDMap) {
      const node = newNodes.find((node) => node.id.substring(node.id.lastIndexOf('-') + 1) === key)
      if (!node) {
        continue
      }
      const previoustNodeId = node.id
      const newNodeId = node.data.technologyName + '-' + generatedWorkflow.nodeUUIDMap[key]
      node.id = newNodeId
      newEdges.filter((edge) => edge.source === previoustNodeId)?.forEach((edge) => {
        edge.source = newNodeId
      })
      newEdges.filter((edge) => edge.target === previoustNodeId)?.forEach((edge) => {
        edge.target = newNodeId
      })
    }

    setNodes(newNodes)
    setEdges(newEdges)
  }

  // S31/S32 (RN09.4/RN09.5/D5): clicking a BOM job chip only patches the already-rendered nodes'
  // `data.jobHighlighted` (via the pure `applyJobHighlight`) — never rebuilds node data from the
  // workflow model (which would re-trigger `fitView`/pan/zoom) and never touches `selected`.
  // Ajuste PO 2026-07-27: the BOM row the chip belongs to is marked too (same blue as the node), so
  // the tree shows which of its rows the highlighted node came from. Kept here, alongside the graph
  // highlight it mirrors, so both ends stay in sync while the BOM accordion mounts and unmounts.
  // S69/RN30: updating the jobReference must also repaint the node, because the purple is painted from
  // the ReactFlow node's own `data.external` and `UPDATE_JOB_REFERENCE` does not rebuild node data (a
  // rebuild would re-trigger `fitView`, forbidden by RN09.4/RN09.5). The mark is pushed in place, the
  // same technique the BOM highlight uses just below.
  // Story 183 (RN6.4/D12): the visible warning of a mark removed by a technology change. The reducer only
  // counts removals (a reducer must not show anything); the counter lives beside the workflow, never in
  // it, so it does not travel in the save.
  useEffect(() => {
    if (workflowData.nightMarkRemovals > 0) {
      showAlert({ title: t_(NIGHT_MARK_REMOVED_WARNING), severity: 'warning', message: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowData.nightMarkRemovals])

  // Story 183 (RN7): ONE night context for the whole editor, handed to the graph's job panel and to the
  // Process Tree alike, so the two surfaces can never disagree about the same node. The census (RN2,
  // nested subworkflows included) is read on every render because the reducer patches the workflow IN
  // PLACE — its identity is no signal that a mark changed — and the context is memoised on its content.
  const nightJobCensusKey = useNightJobCensusKey(workflowData, nightJobFeatureEnabled)
  const nightJobContext = useMemo(
    () => ({ enabled: nightJobFeatureEnabled, nightJobNodeIds: nightJobCensusKey ? nightJobCensusKey.split('|') : [] }),
    [nightJobFeatureEnabled, nightJobCensusKey]
  )

  const updateJobReference = useCallback((jobReference) => {
    dispatchWorkflowData({ type: 'UPDATE_JOB_REFERENCE', jobReference })
    setNodes(applyExternalMark(nodes, workflowData.currentNode?.id, jobReference))
  }, [dispatchWorkflowData, setNodes, nodes, workflowData.currentNode])

  // Story #150: the task table now lives in the tree too, and it reaches the temporary UUID map the
  // same way the graph's job panel does. Named once here so both surfaces share one reader and one
  // writer instead of two copies that could drift apart.
  const getTempUUID = useCallback((uuid) => {
    const find = workflowData.workflow.tempUUIDMap?.find(uuidmap => uuidmap.referenceUUID === uuid)
    return find?.tempUUID
  }, [workflowData.workflow])

  const setTempUUID = useCallback((uuid) => {
    const find = workflowData.workflow.tempUUIDMap?.find(uuidmap => uuidmap.referenceUUID === uuid)
    if (!find) {
      dispatchWorkflowData({ type: 'SET_TEMP_UUID_FOR_NODE_ID', uuid })
    }
  }, [workflowData.workflow, dispatchWorkflowData])

  // PO review 2026-09-11: the Tree edits without asking for the header's Edit button first — its first
  // change enters edit mode exactly as that button does (`WorkflowHeader.jsx`): ping, SET_EDITING, callback.
  // Historia 161: mientras hay un guardado en vuelo el árbol tampoco puede entrar en edición por su
  // cuenta, o su primer cambio se colaría en mitad del guardado.
  const canEnterEditMode = !!allowEdit && !workflowData.locked && !isSaving
  const enterEditMode = useCallback(() => {
    if (workflowData.editing || !canEnterEditMode) {
      return
    }
    if (workflowData.workflow?.id) {
      apiGet('workflows/ping/' + workflowData.workflow.id)
    }
    dispatchWorkflowData({ type: 'SET_EDITING', editing: true })
    onEditModeChange?.(true)
  }, [workflowData.editing, workflowData.workflow, canEnterEditMode, dispatchWorkflowData, onEditModeChange])

  const highlightJobNode = useCallback((assignment, workflowResourceUUID = '') => {
    const targetNodeId = resolveHighlightTargetNodeId(assignment)
    setHighlightedResourceUUID(workflowResourceUUID)
    setNodes(applyJobHighlight(nodes, targetNodeId))
  }, [nodes, setNodes])

  // S69/S70/S71 (historia 152, RN-02.6/RN-02.7): selecting an ASSEMBLY in the BOM tree highlights
  // EVERY operation that consumes it — the tree resolves which ones (E8) — plus the connections
  // between them: the edges whose TWO ends are highlighted (`edgesBetween`). Same technique and
  // same blue as the chip highlight above, one cardinality up: the already-rendered nodes and
  // edges are patched in place, so nothing is selected, nothing is rebuilt (no `fitView`, no
  // pan/zoom) and NOTHING is dispatched to the reducer — the highlight is not a change of the
  // workflow (IN14) and must never travel in the save.
  const highlightAssemblyNodes = useCallback((nodeIds, workflowResourceUUID = '') => {
    setHighlightedResourceUUID(workflowResourceUUID)
    setNodes(applyJobHighlight(nodes, nodeIds))
    setEdges(applyEdgeHighlight(edges, edgesBetween(edges, nodeIds)))
  }, [nodes, setNodes, edges, setEdges])

  // Ajuste PO 2026-07-27: clicking outside the BOM tree drops the selection at BOTH ends — a blue
  // node border with nothing marked in the tree is exactly the ambiguity the row mark removed.
  // `applyJobHighlight` with no target node clears the flag on every node, and the assembly
  // highlight of #152 goes out with it, edges included: the graph goes back to exactly the
  // presentation it had.
  const clearJobHighlight = useCallback(() => {
    setHighlightedResourceUUID('')
    setNodes(applyJobHighlight(nodes, ''))
    setEdges(applyEdgeHighlight(edges, new Set()))
  }, [nodes, setNodes, edges, setEdges])

  const onAddRecord = (newRecord) => {
    if (allowEdit) {
      markUnsavedChanges()
    }
    dispatchWorkflowData({ type: 'SET_WORKFLOW', workflow: newWorkflow(), editing: true })
  }
  const handleSubWorkflowDoubleClick = async (event, node) => {
    const nodeData = workflowData.workflow.nodes.find((item) => item.id === node.id)
    if (nodeData.nodeType !== 'WORKFLOW') {
      return
    }
    setSubworkflowNodeId(node.id)
    if (nodeData.subWorkflow.nodes) {
      // Reuse previous data
      setSubworkflowData(nodeData.subWorkflow)
      return
    }
    const workflowId = node?.data?.referenceId
    if (!workflowId) return
    const response = await apiGet('workflow/' + workflowId)
    response.tempWorkflowUUID = getRandomUUID()
    clearTempDocumentScopes(response)
    // HU-05: what was just loaded is the baseline of the unit's edits, with the updates already accepted for it showing their target.
    setSubworkflowData(templateActions.onUnitLoaded(node.id, response))
  }

  const revert = () => {
    if (workflowData.workflow.id) {
      apiGet('workflows/unLock/' + workflowData.workflow.id)
    }
    // Discard if we were creating
    if (!workflowData.workflow.id) {
      clearUnsavedChanges()
      dispatchWorkflowData({ type: 'SET_WORKFLOW', workflow: newWorkflow(), editing: false })
      return
    }
    // Load again same data from originalData. HU-13: on a fresh temp scope, so a BOM imported
    // during the discarded edit (which lives server-side under the current tempWorkflowUUID, not
    // in this model) stops being read back in place of the saved one.
    clearUnsavedChanges()
    dispatchWorkflowData({ type: 'SET_WORKFLOW', workflow: buildRevertedWorkflow(originalData), editing: false })
  }

  /**
   * The error path of a failed save of a new version (S63/D20/E6 duplicated planning group; historia 152 contract literals, which ARE
   * the i18n key; any other message as Sun sent it). Story 224 (S131): also the path of a save that carries template updates.
   */
  const showSaveError = (error) => {
    if (isDuplicatePlanningGroupSaveError(error)) {
      showAlert({
        severity: 'error',
        message: t_(DUPLICATE_PLANNING_GROUP_MESSAGE_KEY, { values: extractDuplicatePlanningGroupValues(error).join(', ') })
      })
      return
    }
    const contractError = readProcessSaveError(error)
    if (contractError) {
      showAlert({ title: t_('Error updating job reference:'), severity: 'error', message: t_(contractError) })
      return
    }
    showAlert({ title: t_('Error updating job reference:'), severity: 'error', message: t_(error.message) })
  }

  /**
   * Historia 161 (CU6 paso 3, RN19/P2): "Reload" on the version-conflict dialog. Order matters
   * (CU6-A2, GATE 2): discard the unsaved changes and release the edit reservation of the STARTING
   * workflow FIRST, then attempt to open the vigent version — so even when the `GET` below fails
   * (S44) the changes are already unrecoverable and the reservation already released, exactly like
   * Revert's own discard-and-unlock. On success this simply replaces the just-reverted workflow
   * with the reloaded one, still outside edit mode (A3-style: the editor lands ON the response).
   */
  const openWorkflowVersion = async (targetWorkflowId) => {
    const startingWorkflowId = workflowData.workflow.id
    clearUnsavedChanges()
    dispatchWorkflowData({ type: 'SET_WORKFLOW', workflow: buildRevertedWorkflow(originalData), editing: false })
    if (startingWorkflowId) {
      await apiGet('workflows/unLock/' + startingWorkflowId)
    }
    try {
      const reloaded = await apiGet('workflow/' + targetWorkflowId)
      if (reloaded?.id && !reloaded.error) {
        reloaded.tempWorkflowUUID = getRandomUUID()
        dispatchWorkflowData({ type: 'SET_WORKFLOW', workflow: reloaded, editing: false })
        return true
      }
      // S44: shown like any other failed load of the editor — the reverted (starting) workflow,
      // already outside edit mode, stays on screen.
      showAlert({ title: t_('Error loading data'), severity: 'error', message: t_('Error loading data') })
    } catch (loadError) {
      showAlert({ title: t_('Error loading data'), severity: 'error', message: t_(loadError.message) })
    }
    return false
  }

  const reloadAfterWorkflowVersionConflict = async () => {
    await openWorkflowVersion(workflowVersionConflict.latestWorkflowId)
    setWorkflowVersionConflict({ open: false, latestWorkflowId: 0, latestVersion: 0 })
  }

  /** Historia 161 (CU6-A1): "Stay in the editor" — closes the dialog, touches nothing else. */
  const stayInEditorAfterWorkflowVersionConflict = () => {
    setWorkflowVersionConflict({ open: false, latestWorkflowId: 0, latestVersion: 0 })
  }
  useEffect(() => {
    if (workflowData.workflow?.id) {
      hierarchy.registerWorkflow(workflowData.workflow.id, parentWorkflowId)

      const registerSubWorkflows = (workflow, parentId) => {
        workflow.nodes?.forEach(node => {
          if (node.subWorkflow?.id) {
            const subWorkflowIds = node.subWorkflow.subWorkflowIds || []
            hierarchy.registerChildWorkflow(parentId, node.subWorkflow.id, subWorkflowIds)
            registerSubWorkflows(node.subWorkflow, node.subWorkflow.id)
          }
        })
      }

      registerSubWorkflows(workflowData.workflow, workflowData.workflow.id)

      return () => {
        hierarchy.unregisterWorkflow(workflowData.workflow.id, parentWorkflowId)
      }
    }
  }, [workflowData.workflow?.id, parentWorkflowId, hierarchy, workflowData?.workflow])

  useEffect(() => {
    if (onWorkflowChange) {
      onWorkflowChange(workflowData.workflow)
    }
  }, [workflowData.workflow, onWorkflowChange])

  /**
   * The user accepts the `SubworkflowDialog` of the node `subworkflowNodeId`. HU-05 (DL15): if that node is a linked unit and the dialog edited it,
   * the user is asked "publish or unlink" first; Cancel leaves the dialog open with its edits (resolves `false`).
   */
  const onAcceptSubworkflow = async (subworkflow, nodes) => {
    if (!(await templateActions.onAcceptUnit(subworkflowNodeId, subworkflow))) {
      return false
    }
    // G4/H15: what this dialog deleted (a unit, or a container with units in it) takes its pending updates and decisions with it.
    const deletedNodes = deletedUnitNodes((subworkflowData || {}).nodes || [], subworkflow.nodes || [])
    if (deletedNodes.length > 0) {
      templateActions.onUnitsDeleted(deletedNodes)
    }
    const node = workflowData.workflow.nodes.find((node) => node.id === subworkflowNodeId)
    node.subWorkflow = subworkflow
    node.subWorkflow.workflowPositions = updatePositions(nodes, node.subWorkflow)
    dispatchWorkflowData({ type: 'UPDATE_CURRENT_NODE', node })
    return true
  }

  if (workflowUseIndex === null || workflowUseIndex === undefined) {
    return <CircularProgress />
  }

  // Ajuste PO 2026-07-27: the section formerly titled "Tasks" is the selected job's general
  // properties, and its title now says WHICH job those are. It only ever applies to a JOB node (it
  // is disabled otherwise), and RN01.6/RNT5 makes the node's code its functional identifier and its
  // name a free descriptive field — both belong in the title.
  const selectedJobNode = workflowData.currentNode?.nodeType === 'JOB' ? workflowData.currentNode : {}

  // Historia 152 (S27 · RN-01.13b, RN-G5): el proceso de origen de un nodo WORKFLOW seleccionado.
  // Es el pendiente que J1 dejó abierto: el bloque "Origin" estaba construido pero el acordeón de
  // "General Properties" está deshabilitado para los nodos WORKFLOW, así que un subproceso
  // arrastrado de una plantilla no tenía DÓNDE mostrar su origen. Con esto lo tiene, en SOLO
  // LECTURA y sin abrir nada más de ese panel.
  //
  // D12: lo que se lee es el PAR, ya normalizado (`IN16`): medio par no es un origen y no abre el
  // acordeón — el panel no puede rotular "⟨código⟩ v⟨n⟩" sin las dos mitades.
  const selectedWorkflowNodeOrigin = workflowData.currentNode?.nodeType === 'WORKFLOW'
    ? nodeSourceProcessPair(workflowData.currentNode)
    : { sourceProcessCode: '', sourceProcessVersionNumber: null }
  const selectedWorkflowNodeHasOrigin = selectedWorkflowNodeOrigin.sourceProcessCode !== ''
  // Story 224 (S104, S105, S145): the template link of the selected WORKFLOW node (E1); `{}` for any other node.
  const selectedWorkflowNodeTemplateLink = workflowData.currentNode?.nodeType === 'WORKFLOW'
    ? linkOfNode(templateLinks.links, workflowData.currentNode.id)
    : {}
  const selectedWorkflowNodeHasTemplateLink = !!selectedWorkflowNodeTemplateLink.templateCode

  // Historia 130 (S9, CA1.4 / RN-01.7): mientras el tipo elegido es OPERATIONS el lienzo del grafo
  // no se muestra y su sitio lo ocupa la biblioteca. Una operación de biblioteca no tiene grafo:
  // ni aristas, ni padre, ni layout (I11/D12), así que no hay nada que pintar ahí.
  const isOperationsView = workflowData.type?.code === 'OPERATIONS'
  const treeShown = showProcessTree && surface === 'tree' && !isOperationsView
  const canEditOperationLibrary = isUserAuthorized('OPERATION_LIBRARY_EDIT')
  // S8 (CA1.3 / RN-01.6): con `OPERATION_LIBRARY` pero sin `OPERATION_LIBRARY_EDIT` los cuatro
  // acordeones se ven en SOLO LECTURA. Es gating de UI: Sun responde 403 a las rutas de escritura
  // aunque el cliente las invoque igualmente (E-PRM-02).
  //
  // El permiso de la biblioteca es el ÚNICO gating de la vista OPERATIONS: el modo edición del
  // editor es del workflow suspendido y no tiene nada que decir sobre una operación de biblioteca
  // (exigirlo dejaba la vista en solo lectura cuando se entraba sin workflow cargado).
  // Historia 161 (Enmienda 2): title of the saving overlay, built like the "Saving fabrication order"
  // one (`FabricationOrder.jsx`) but with the existing `Saving` key (no new i18n keys, RN31).
  const savingTitle = createDialogTitle({ color: 'info', title: t_('Saving'), icon: HourglassTopOutlined })

  const accordionEditing = isOperationsView ? canEditOperationLibrary : workflowData.editing

  // Historia 152 · **Enmienda 2** (PO, 22/09/2026 · S15): un nodo con una COPIA LOCAL PENDIENTE
  // enseña el job de origen previsualizado, pero no deja editarlo. Al guardar, Sun descarta el
  // contenido del destino y lo sustituye por la copia literal del origen
  // (`PostgresJobGraphRepository#copyWorkflowJobIntoWorkflowJob:179-181`: `deleteChildren(target)`,
  // `deleteJobDocuments`, `copyScalars`), así que lo que se escribiera aquí se perdería EN SILENCIO
  // — el defecto que el PO reportó. Es la forma mínima: el mismo `editing` que ya reparte el gating
  // de #130 S8, apagado para los tres acordeones del job de ESE nodo. Lo demás del editor —las
  // conexiones del grafo, la posición, el code y el name del nodo, el BOM del workflow— no vive en
  // el `jobReference` que Sun sobrescribe y sigue como siempre.
  //
  // ⚠️ Los cuatro campos tolerados (`jobOrder`, `positionInLine`, `planningGroup` e `indexValue`)
  // quedan bloqueados CON el resto, a diferencia de la letra de la enmienda: `copyScalars` los
  // copia también (`JobReferenceEntityCopier.java:36,47-49`), así que dejarlos abiertos habría
  // reproducido exactamente la pérdida silenciosa que esto viene a cerrar. Declarado en `log.md` §J10.
  //
  // Story 167 (PO, 29/09/2026): a copy whose source could be read travels with `keepEditedContent`, and Sun
  // then keeps what the node carries and copies only the attachments — so it is edited like any other job.
  // Only the placeholder of an unreadable source is still overwritten on save, and stays read-only.
  const copyLink = workflowData.currentNode?.operationLink
  const hasPendingLocalCopy = copyLink?.intent === 'COPY' && copyLink?.keepEditedContent !== true
  const jobAccordionEditing = accordionEditing && !hasPendingLocalCopy

  // S7 (CA1.3 / RN-01.5): seleccionar una versión la carga en los MISMOS cuatro acordeones que
  // editan un nodo del grafo. El nodo que se les da es sintético: la fila de biblioteca no está en
  // `workflow.nodes` y nunca entra en el grafo.
  const loadOperationVersion = async (version) => {
    if (!version?.nodeId) return
    try {
      // `OperationVersionDetail` = { version, jobReference } (§8.4): la cabecera y el DTO del editor
      // reutilizado verbatim, con `status` nulo y sin filas WORKFLOW_RESOURCE.
      const detail = await getOperationVersionDetail(version.nodeId)
      const header = { ...version, ...(detail?.version || {}) }
      setSelectedOperationVersion(header)
      dispatchWorkflowData({
        type: 'SET_CURRENT_NODE',
        node: {
          id: version.nodeId,
          nodeType: 'JOB',
          code: header.code || '',
          name: header.name || '',
          jobReference: detail?.jobReference || {}
        }
      })
      readCurrentOperationVersion(header)
    } catch (thrown) {
      showAlert({ title: t_(readOperationLibraryError(thrown).error || 'Not allowed'), severity: 'error' })
    }
  }

  // Historia 184 (CA7 · RN-05.4, RN-05.6, RN-05.9): al cargar una versión se pregunta a N2 cuál es la vigente
  // de su código (la de número más alto, sea cual sea su Index Value) y se guarda en la propia versión cargada.
  // No bloquea la carga ni enseña nada si falla (D2): el aviso es una ayuda. La guardia por `nodeId` dentro del
  // updater descarta la respuesta tardía de una carga anterior cuando el usuario ya ha cargado otra versión.
  const readCurrentOperationVersion = (header) => {
    if (!header?.code || !header?.nodeId) return
    Promise.resolve()
      .then(() => getOperationDetail(header.code))
      .then((detail) => currentVersionNumberOf(detail))
      .then((currentVersionNumber) => {
        setSelectedOperationVersion((current) => (current.nodeId === header.nodeId && !current.isNew
          ? { ...current, currentVersionNumber }
          : current))
      })
      .catch(() => {})
  }

  // Ajuste del PO (2026-09-15): "New operation" no escribe nada. Siembra un JOB completo con la
  // MISMA fábrica que usa el arrastre de una tecnología al lienzo —de ahí salen `operatorQuantity`,
  // `minimumQuantity`, la preparación y la primera tarea, que Sun exige no nulos— y lo deja en los
  // cuatro acordeones para configurarlo. Lo escribe el Save de la vista (N8).
  const seedOperationDraft = (request) => {
    const jobReference = createNewJobReference({
      technologyName: request.technology?.name,
      technologies: [request.technology],
      indexValues,
      progressCalculationTypes
    })
    if (request.indexValue) {
      jobReference.indexValue = indexValues.find((index) => index.name === request.indexValue) || jobReference.indexValue
    }
    setOperationCreateError('')
    setSelectedOperationVersion({
      nodeId: '',
      code: request.code,
      name: request.name,
      description: request.description,
      indexValue: request.indexValue,
      isNew: true
    })
    dispatchWorkflowData({
      type: 'SET_CURRENT_NODE',
      node: { id: 'new-operation', nodeType: 'JOB', code: request.code, name: request.name || '', jobReference }
    })
  }

  // S11 (RN-02.12 ampliada): con cambios pendientes, cambiar de versión pasa SIEMPRE por C11. Sun no
  // participa: no hay 409 ni literal — las dos salidas son el commit (N8) y el revert (N9).
  const onOperationVersionSelected = (version) => {
    if (workflowData.operationPendingChanges) {
      setPendingVersionChange(version)
      return
    }
    loadOperationVersion(version)
  }

  // S22 (RN-02.11, Δ9): el Revert propio de la vista OPERATIONS descarta TODO lo acumulado en su
  // `scopeUUID`, que es SEPARADO del `tempWorkflowUUID` del editor de workflows.
  const revertOperationScope = async () => {
    try {
      await discardOperationScope(operationScopeUUID)
    } catch (thrown) {
      showAlert({ title: t_(readOperationLibraryError(thrown).error || 'Not allowed'), severity: 'error' })
      return
    }
    setOperationScopeUUID(getRandomUUID())
    dispatchWorkflowData({ type: 'OPERATION_SCOPE_SETTLED' })
    setOperationReloadKey((key) => key + 1)
    setSelectedOperationVersion({})
    dispatchWorkflowData({ type: 'SET_CURRENT_NODE', node: null })
  }

  const commitOperationVersion = async (saveMode, indexValue) => {
    try {
      await commitOperationScope(operationScopeUUID, {
        // `targetCode` null ⇒ operación NUEVA (§8.4); con código, versión nueva de esa operación.
        targetCode: selectedOperationVersion.isNew ? null : (selectedOperationVersion.code || null),
        saveMode,
        baseNodeId: selectedOperationVersion.isNew ? null : (selectedOperationVersion.nodeId || null),
        code: selectedOperationVersion.code || '',
        name: selectedOperationVersion.name || '',
        description: selectedOperationVersion.description || '',
        indexValue,
        jobReference: workflowData.currentNode?.jobReference || {}
      })
      setOperationSaveMode(null)
      setOperationScopeUUID(getRandomUUID())
      dispatchWorkflowData({ type: 'OPERATION_SCOPE_SETTLED' })
      setOperationReloadKey((key) => key + 1)
      showAlert({ title: t_('Saved correctly'), severity: 'success' })
      setOperationCreateError('')
      if (pendingVersionChange) {
        // El guardado venía de C11: lo que el usuario quería era abrir OTRA versión.
        const next = pendingVersionChange
        setPendingVersionChange(null)
        await loadOperationVersion(next)
        return
      }
      // Guardar cierra la edición, igual que el revert: se vuelve al listado y el Save y el Revert
      // dejan de ofrecerse, porque ya no hay nada que escribir ni que descartar.
      setSelectedOperationVersion({})
      dispatchWorkflowData({ type: 'SET_CURRENT_NODE', node: null })
    } catch (thrown) {
      const { error, context } = readOperationLibraryError(thrown)
      // Un alta rechazada (E-OPL-01/E-OPL-02/E-OPL-03) vuelve al diálogo de alta, que es donde están
      // los campos que hay que corregir; no al diálogo de modo de guardado, que ahí no pinta nada.
      if (selectedOperationVersion.isNew) {
        setOperationCreateError(error || 'Not allowed')
        return
      }
      // E-OPL-04: Sun devuelve en el cuerpo los modos que SÍ quedan; el diálogo se reabre con ellos.
      setOperationSaveMode({
        allowedSaveModes: Array.isArray(context.allowedSaveModes) ? context.allowedSaveModes : ['NEW_VERSION'],
        defaultIndexValue: indexValue,
        error
      })
    }
  }

  // K27: el Save propio de la vista OPERATIONS. Pregunta primero a N4 qué salidas quedan abiertas
  // ahora mismo — que dependen solo de si la versión está en uso (RN-02.4).
  // S73/S84/S116 (CA4.1 / Δ9, HU-07): guardar un job en la biblioteca es un cambio PENDIENTE del
  // nodo. Lo escribe el guardado general del workflow con la intención PUBLISH; por eso un revert lo
  // deja sin efecto y la operación nunca llega a existir. El `jobReference` del nodo NO se toca
  // (RN-G4/RN-07.2): el nodo sigue ejecutando lo suyo y lo que se crea es una COPIA.
  const publishOperationFromNode = (publish) => {
    const node = workflowData.currentNode
    if (!node) return
    markUnsavedChanges()
    dispatchWorkflowData({
      type: 'UPDATE_CURRENT_NODE',
      node: { ...node, operationLink: { ...(node.operationLink || {}), intent: 'PUBLISH', publish } }
    })
  }

  // S91/S96 (CA5.2 / RN-05.10, Δ9): confirmar la actualización NO la persiste. Queda como cambio
  // pendiente del nodo (`intent: 'UPDATE'`) y la escribe el guardado general; por eso el revert la
  // descarta y el aviso vuelve a mostrarse.
  //
  // Lo que SÍ pasa en el momento es la PREVISUALIZACIÓN: el job del nodo se sustituye por el de la
  // versión destino conservando lo que es del nodo (los cuatro campos de configuración, el status y
  // las asignaciones de BOM), que es lo mismo que hará el servidor al guardar (RN-05.9). Sin esto los
  // acordeones seguían enseñando el contenido viejo hasta recargar el workflow.
  const updateNodeOperation = async (targetNodeId) => {
    const node = workflowData.currentNode
    if (!node || !targetNodeId) return
    let jobReference = node.jobReference
    try {
      const detail = await getOperationVersionDetail(targetNodeId)
      // Sin contenido de la versión no se previsualiza nada, pero tampoco se vacía el nodo: se deja
      // el job que tiene y el servidor copiará igual al guardar.
      if (detail?.jobReference) {
        // Story 186 (S165, S166): in CONTROLLED mode each feature group shows the state and version the update leaves
        jobReference = asUpdatedOperationJob(node.jobReference, detail.jobReference, {
          controlled: featureGroupRevisionControlled,
          baselines: workflowData.featureGroupBaselines,
          draftIndexValue: indexValues.find((index) => index.name === 'DRAFT')
        })
      }
    } catch (thrown) {
      // Sin el contenido de la versión se puede seguir: el servidor copia igual al guardar. Lo que se
      // pierde es la previsualización, así que se dice.
      showAlert({ title: t_(readOperationLibraryError(thrown).error || 'Not allowed'), severity: 'warning' })
    }
    // Story 186 (S171, E2-8): the groups the update leaves degrade the job, and the job the workflow, like a group moved to
    // DRAFT by hand (UPDATE_TASKS, S87): same rule, `indexDegradation.js`. It never promotes; Sun re-bounds the job on save.
    if (featureGroupRevisionControlled) {
      jobReference = { ...jobReference, indexValue: resolveDegradedIndexValue(jobReference.indexValue, collectFeatureGroupIndexValues(jobReference)) }
    }
    markUnsavedChanges()
    dispatchWorkflowData({
      type: 'UPDATE_CURRENT_NODE',
      degradeWorkflow: featureGroupRevisionControlled,
      node: {
        ...node,
        jobReference,
        operationLink: { ...(operationLinks[node.id] || node.operationLink || {}), intent: 'UPDATE', libraryNodeId: targetNodeId }
      }
    })
  }

  // Historia 152 · S47 (CA1.11 / RN-01.17, RN-01.18, Δ9): desvincular es una ORDEN del usuario
  // (§8.3: `UNBIND` dejó de ser una pista) y, como todo lo que esta historia añade al editor, un
  // cambio PENDIENTE (RN-G12): no existe endpoint de desvincular (§8.6). Lo único que queda en el
  // nodo es la intención; el estado final —`UNLINKED` con `originCode` / `originVersionNumber`— lo
  // escribe el guardado general y lo devuelve el servidor, que es quien fija la cabecera J19.
  //
  // El `jobReference` NO se toca: desvincular no cambia el contenido del job, solo de dónde dice
  // que viene (RN-G4).
  const unlinkNodeOperation = () => {
    const node = workflowData.currentNode
    if (!node) return
    markUnsavedChanges()
    dispatchWorkflowData({
      type: 'UPDATE_CURRENT_NODE',
      node: { ...node, operationLink: { ...(operationLinks[node.id] || node.operationLink || {}), intent: 'UNBIND' } }
    })
  }

  // Historia 152 · S29 (CA1.7 / RN-01.13c): ir a la ficha de la operación es cambiar a la vista
  // OPERATIONS —la tercera opción del selector de la cabecera, de #130— con la versión vinculada ya
  // cargada en los cuatro acordeones. El editor se SUSPENDE, no se guarda ni se revierte: volver al
  // grafo lo restaura con sus cambios pendientes (`suspendedEditor`, S10).
  const openOperationInLibrary = (target) => {
    dispatchWorkflowData({ type: 'UPDATE_TYPE', editionType: { code: 'OPERATIONS', name: t_('Operations') } })
    if (target?.nodeId) loadOperationVersion(target)
  }

  // S105/S107 (CA5.4 / RN-05.7, N15): descartar SÍ se escribe en el momento, porque el descarte es
  // del vínculo y compartido entre usuarios — no una preferencia local de quien lo pulsa.
  const dismissNodeOperationUpdate = async (versionNumber) => {
    const node = workflowData.currentNode
    if (!node || !versionNumber) return
    try {
      const link = await dismissOperationUpdate(workflowData.workflow.id, node.id, versionNumber)
      setOperationLinks((current) => ({ ...current, [node.id]: link }))
    } catch (thrown) {
      showAlert({ title: t_(readOperationLibraryError(thrown).error || 'Not allowed'), severity: 'error' })
    }
  }

  const startOperationSave = async () => {
    // Una operación NUEVA no tiene nada que sobrescribir: no hay modo que preguntar (N4), se crea.
    if (selectedOperationVersion.isNew) {
      await commitOperationVersion('NEW_VERSION', selectedOperationVersion.indexValue || 'APPROVED')
      return
    }
    if (!selectedOperationVersion.nodeId) return
    try {
      const options = await getOperationVersionSaveMode(selectedOperationVersion.nodeId)
      setOperationSaveMode({
        allowedSaveModes: options?.allowedSaveModes?.length ? options.allowedSaveModes : ['NEW_VERSION'],
        defaultIndexValue: options?.indexValue || selectedOperationVersion.indexValue || '',
        error: ''
      })
    } catch (thrown) {
      showAlert({ title: t_(readOperationLibraryError(thrown).error || 'Not allowed'), severity: 'error' })
    }
  }

  return (
    <Stack sx={{ border: workflowData.editing ? '1px solid ' + theme.palette.primary.main : '' }}>
      <SubworkflowDialog
        open={subworkflowData !== null}
        setOpen={() => setSubworkflowData(null)}
        initialWorkflowData={subworkflowData}
        frontendConfig={frontendConfig}
        acceptSubworkflow={onAcceptSubworkflow}
        editMode={workflowData.editing}
        onEditModeChange={(isEditing) => dispatchWorkflowData({ type: 'SET_EDITING', editing: isEditing })}
        parentPartReference={workflowData.partReference}
        allowEdit={allowEdit && !workflowData.locked}
        parentWorkflowCode={workflowData.workflow?.code || ''}
        parentWorkflowName={workflowData.workflow?.name || ''}
        inheritedTemplateLinks={{ ...templateLinks, unitNodeId: subworkflowNodeId || '', actions: templateActions, materialization: templateMaterialization }}
      />
      {/* Enmienda 2 (E2-D1/RN31): `position: relative` is what CONTAINS the `Backdrop` below to this
          editor instead of the whole window (MUI's default `position: fixed`, RE2-1). The `Backdrop`
          itself is a SIBLING of `editorContentRef`'s wrapper, not a descendant of it: `inert` (RN32)
          removes its whole subtree from the accessibility tree, and the "Saving" indicator has to
          stay perceivable while it covers everything else (header through documents, CA21). */}
      <Stack sx={{ position: 'relative' }}>
        <Stack ref={editorContentRef} data-testid='workflow-editor-content'>
          <Card sx={{ width: '100%', margin: 0, padding: '1rem', position: 'sticky', top: 0, zIndex: 50, backgroundColor: (theme) => theme.palette.background.paper + 'AA' }}>
            <Stack gap={3}>
              {AlertElement}
              <WorkflowHeader
                workflowEditingData={workflowData}
                dispatchWorkflowData={dispatchWorkflowData}
                // K27/K28 (S22): en la vista OPERATIONS el Save y el Revert de la cabecera son los de
                // la biblioteca — commit y revert del `scopeUUID` propio, no del workflow.
                setSaveDialogOpen={isOperationsView ? startOperationSave : handleOpenSaveDialog}
                onRevert={isOperationsView ? revertOperationScope : revert}
                isOperationsView={isOperationsView}
                // Save y Revert de la biblioteca: solo tienen sentido con una operación cargada —un
                // alta recién preparada o una versión elegida—. Sin ella no hay nada que escribir ni
                // que descartar, así que la cabecera no los pinta.
                operationsEditing={canEditOperationLibrary && !!(selectedOperationVersion.nodeId || selectedOperationVersion.isNew)}
                onAddRecord={onAddRecord}
                saveAllowed={saveAllowed}
                saveBlockedReason={t_('Finish editing the open row before saving')}
                hideButtonsSubWorkflow={hideButtonsSubWorkflow}
                fromFO={fromFO}
                onEditModeChange={onEditModeChange}
                indexValues={indexValues}
                workflowUseIndex={workflowUseIndex}
                cascadeApprovalEnabled={cascadeApprovalEnabled}
                featureGroupRevisionControlled={featureGroupRevisionControlled}
                allowEdit={allowEdit}
                isSaving={isSaving}
                showAlert={showAlert}
                templateLink={isNestedTemplateView ? {} : rootLinkOf(templateLinks.links)}
              />
            </Stack>
          </Card>
          {/* Story 224 (S120): what updating the unit does; confirming leaves it pending for the Save (adjustment 2026-10-29). */}
          <TemplateUpdateImpactDialog
            impact={templateImpactDialog.impact}
            viewOnly={templateImpactDialog.viewOnly}
            discardedOperationJobs={templateImpactDialog.discardedOperationJobs}
            onCancel={() => setTemplateImpactDialog(NO_TEMPLATE_IMPACT_DIALOG)}
            onConfirm={confirmTemplateUpdate}
          />
          {/* HU-05 (S191-S195, S199): publish the changes of an edited linked unit as a new version of its template, or unlink it. */}
          <TemplateLinkDecisionDialog
            rows={decisionDialog.rows}
            single={decisionDialog.single}
            useIndex={!!workflowUseIndex}
            indexValues={indexValues}
            onDecide={decideTemplateUnit}
            onCancel={cancelTemplateDecisions}
          />
          {/* Story 224 (S107): the notice of a newer template version of the workflow being viewed, above the canvas. */}
          <TemplateUpdateBanner links={viewedTemplateLinks} {...templateActions} />
          {/* RN5/INV4 (risk R1): HIDDEN, never unmounted — unmounting would wipe the shared selection,
              the node array the editor saves with, and the pan and zoom of the canvas. */}
          <div style={{ display: treeShown ? 'none' : undefined }}>
            <Stack sx={{ backgroundColor: (theme) => theme.palette.background.paper }} />
            {/* S9: la tabla OPERATIONS ocupa el sitio del acordeón del grafo mientras dura la vista. */}
            {isOperationsView
              ? (
                <OperationLibraryTable
                  useIndex={!!workflowUseIndex}
                  canEdit={canEditOperationLibrary}
                  selectedNodeId={selectedOperationVersion.nodeId || ''}
                  onVersionSelected={onOperationVersionSelected}
                  onCreateDraft={seedOperationDraft}
                  createError={operationCreateError}
                  onCommitted={() => setOperationScopeUUID(getRandomUUID())}
                  reloadKey={operationReloadKey}
                />
                )
              : null}
          </div>
          <Box sx={{ position: 'relative' }}>
            {/* S9/S10 (RN-01.7/RN-01.8): oculto, NUNCA desmontado — desmontarlo se llevaría por
                delante los nodos y el zoom del lienzo, que S10 exige encontrar intactos al volver. */}
            <div data-testid='workflow-graph-accordion' style={{ display: isOperationsView || treeShown ? 'none' : undefined }}>
              <DefaultAccordion
                headerRender={
                  <Stack direction='row' alignItems='center' spacing={2} sx={{ width: '100%', minWidth: 0 }}>
                    <Box component='span' sx={{ flexGrow: 1, minWidth: 0 }}>
                      {buildWorkflowSectionTitle(
                        t_(hideButtonsSubWorkflow && !fromFO ? 'Subworkflow' : 'Workflow'),
                        parentWorkflowCode,
                        parentWorkflowName
                      )}
                    </Box>
                  </Stack>
                }
                defaultExpanded
                disableUnmount
                // The canvas starts just under the bar: the 2em of every other accordion was a band of nothing here (PO, 28/09/2026).
                contentPadding='8px'
              >
                {/* RN5/INV4 (risk R1): the graph is HIDDEN in Tree, never unmounted — it keeps nodes, selection, pan and zoom. */}
                <div data-testid='workflow-graph-surface' style={{ display: treeShown ? 'none' : undefined }}>
                  <div ref={graphBoxRef} style={{ width: '100%', height: surfaceHeight, display: 'grid' }}>
                    <WorkflowFlow
                      workflowData={workflowData}
                      dispatchWorkflowData={dispatchWorkflowData}
                      updateReactFlow={updateReactFlow}
                      setUpdateReactFlow={setUpdateReactFlow}
                      nodes={nodes}
                      setNodes={setNodes}
                      onNodesChange={onNodesChange}
                      edges={edges}
                      setEdges={setEdges}
                      onEdgesChange={onEdgesChange}
                      progressCalculationTypes={progressCalculationTypes}
                      onSubWorkflowDoubleClick={handleSubWorkflowDoubleClick}
                      frontendConfig={frontendConfig}
                      showAlert={showAlert}
                      hierarchy={hierarchy}
                      indexValues={indexValues}
                      fabricationOrderOperable={fabricationOrderOperable}
                      onOpenJobProperties={openJobProperties}
                      onOpenJobResources={openJobResources}
                    />
                  </div>
                  {showProcessTree ? <SurfaceResizeHandle height={surfaceHeight} surfaceRef={graphBoxRef} onHeightChange={setSurfaceHeight} /> : null}
                </div>
              </DefaultAccordion>
            </div>
            {showProcessTree
              ? (
                <div data-testid='workflow-tree-surface' style={{ display: treeShown ? undefined : 'none' }}>
                  <ProcessTreeView
                    key={workflowKey}
                    workflow={workflowData.workflow}
                    currentNode={workflowData.currentNode}
                    dispatchWorkflowData={dispatchWorkflowData}
                    nodes={nodes}
                    setNodes={setNodes}
                    frontendConfig={frontendConfig}
                    isUserAuthorized={isUserAuthorized}
                    showAlert={showAlert}
                    detailedBranches={detailedBranches}
                    active={surface === 'tree'}
                    // Story 224 (DL25-a): what the tree loads on its own shows the target of the updates already accepted, as the unit's dialog does.
                    loadedSubWorkflowContent={withAcceptedUpdates}
                  // PO review 2026-09-10: the tree edits with the SAME mode, writers and Save as the graph's job panel.
                    editing={workflowData.editing}
                    canEnterEditMode={canEnterEditMode}
                    onEnterEditMode={enterEditMode}
                    updateJobReference={updateJobReference}
                    updateTaskList={(taskReferences) => dispatchWorkflowData({ type: 'UPDATE_TASKS', taskReferences })}
                  // Story #150: the job dialog of the tree hosts the full resources panel, so it gets the
                  // very same catalogue and part reference the graph's own panel does.
                    resourceTypes={resourceTypes}
                    parentPartReference={parentPartReference}
                  // The same temporary UUID map, part reference and workflow name the graph's job panel gets. The extracted task table reads them in the body of its JSX, so the
                  // tree cannot mount it without them.
                    modelRevision={workflowData.modelRevision}
                    onEditingRowChanged={reportOpenRow}
                    workflowName={workflowData.workflow?.code}
                    partReference={workflowData.partReference}
                    getTempUUID={getTempUUID}
                    setTempUUID={setTempUUID}
                  // PO review 2026-09-11: and the same catalogues its fields select from.
                    progressCalculationTypes={progressCalculationTypes}
                    indexValues={indexValues}
                    workflowUseIndex={!!workflowUseIndex}
                    showMachineTime={showMachineTime}
                    featureGroupRevisionControlled={featureGroupRevisionControlled}
                    featureGroupBaselines={workflowData.featureGroupBaselines}
                    cascadeApprovalEnabled={!!cascadeApprovalEnabled}
                    fabricationOrderOperable={fabricationOrderOperable}
                    nightJobContext={nightJobContext}
                  // #167: the tree's toolbar starts on the height of the "Workflow" bar and leaves the Graph | Tree slot free.
                  // #215: and it shares the graph's height (CA6).
                    headerSlotPx={SURFACE_SWITCH_SLOT_PX}
                    surfaceHeight={surfaceHeight}
                    onSurfaceHeightChange={setSurfaceHeight}
                  />
                </div>
                )
              : null}
            {/* #167: ONE Graph | Tree pair, anchored to the same spot over both views, so it never moves when the view changes. */}
            {showProcessTree && !isOperationsView
              ? (
                <Box sx={{ position: 'absolute', top: 0, right: SURFACE_SWITCH_RIGHT_PX, height: SURFACE_BAR_HEIGHT_PX, display: 'flex', alignItems: 'center', zIndex: 2 }}>
                  <WorkflowSurfaceSwitch surface={surface} onSurfaceChange={setSurface} />
                </Box>
                )
              : null}
          </Box>
          <div style={{ display: treeShown ? 'none' : undefined }}>
            {/* Historia 184 (CA7/CA8): en OPERATIONS, con una versión cargada que no es la vigente, el aviso va justo
                encima de General Properties. Se monta solo mientras el nodo de los acordeones ES esa versión: salir
                de OPERATIONS, revertir o guardar vacían `currentNode` (y la versión), así que desaparece solo. */}
            {isOperationsView && workflowData.currentNode?.id === selectedOperationVersion.nodeId
              ? (
                <OperationVersionNotice
                  nodeId={selectedOperationVersion.nodeId || ''}
                  isNew={!!selectedOperationVersion.isNew}
                  versionNumber={selectedOperationVersion.versionNumber}
                  currentVersionNumber={selectedOperationVersion.currentVersionNumber}
                />
                )
              : null}
            <DefaultAccordion
              headerRender={buildWorkflowSectionTitle(
                t_('General Properties'),
                selectedWorkflowNodeHasOrigin || selectedWorkflowNodeHasTemplateLink ? workflowData.currentNode.code || '' : selectedJobNode.code || '',
                selectedWorkflowNodeHasOrigin || selectedWorkflowNodeHasTemplateLink ? workflowData.currentNode.name || '' : selectedJobNode.name || ''
              )}
              defaultExpanded={false}
              openSignal={generalPropertiesSignal}
              // Historia 152 (S27): un nodo WORKFLOW **con proceso de origen** sí tiene algo que
              // enseñar aquí —su origen—, así que el acordeón se habilita en SOLO LECTURA para él.
              // Sin origen sigue deshabilitado, exactamente como antes de esta historia.
              disabled={!workflowData.currentNode ||
                (workflowData.currentNode.nodeType === 'WORKFLOW' && !selectedWorkflowNodeHasOrigin && !selectedWorkflowNodeHasTemplateLink) ||
                !isUserAuthorized('WORKFLOW_JOBS')}
            >
              {/* Historia 152 (S27 · RN-01.13b, RN-G5): el panel de un nodo WORKFLOW no tiene
                  propiedades de job que mostrar, pero SÍ muestra su proceso de origen. Misma
                  implementación que la del panel de un JOB, montada aquí porque `JobTask` es el
                  editor del job y un subproceso no lo tiene. */}
              {selectedWorkflowNodeHasOrigin || selectedWorkflowNodeHasTemplateLink
                ? (
                  <Stack spacing={1} sx={{ px: 2, pt: 1.5 }}>
                    {selectedWorkflowNodeHasOrigin ? <NodeOriginBlock {...selectedWorkflowNodeOrigin} /> : null}
                    {/* Story 224 (S104, S105, S145): the template and version this subworkflow comes from. */}
                    <TemplateLinkLabel link={selectedWorkflowNodeTemplateLink} testId='node-template-link' />
                    {/* Its notice of a newer template version, with Update and Dismiss, as an operation's notice in its node. */}
                    {selectedWorkflowNodeHasTemplateLink
                      ? <TemplateUpdateBanner links={[selectedWorkflowNodeTemplateLink]} testId='node-template-update-banner' {...templateActions} />
                      : null}
                  </Stack>
                  )
                : <JobTask
                    node={isUserAuthorized('WORKFLOW_TASKS') ? workflowData.currentNode : []}
                // Historia 130 (S56): la foto del job con la que este nodo se vinculó, para que la
                // cabecera pase a "Embedded" en el momento en que un cambio rompe, antes de guardar.
                    linkedJobReference={workflowData.operationLinkBaselines?.[workflowData.currentNode?.id] || null}
                // S73/S76/S116 (HU-07): guardar en biblioteca es del editor del workflow, no de la
                // vista OPERATIONS —ahí el job YA es una operación de biblioteca.
                    canPublishOperation={canEditOperationLibrary && !isOperationsView}
                    workflowId={workflowData.workflow?.id || 0}
                    onOperationPublish={publishOperationFromNode}
                // S85/S91/S105: el estado del vínculo de ESTE nodo (N13) y las dos acciones del aviso.
                // Lo persistido (N13) más la intención que este editor tiene pendiente sobre el nodo:
                // sin fusionarlas, una actualización aceptada no se vería hasta después de guardar.
                    operationLink={resolveNodeOperationLink(
                      operationLinks[workflowData.currentNode?.id],
                      workflowData.currentNode?.operationLink
                    )}
                    onOperationUpdate={updateNodeOperation}
                    onOperationDismiss={dismissNodeOperationUpdate}
                // Historia 152 (S29/S38/S47 · RN-G15): el permiso de LECTURA de la biblioteca es el
                // que abre las acciones del vínculo y la navegación a la ficha. En la vista
                // OPERATIONS no hay nodo del grafo del que desvincularse.
                    canViewOperationLibrary={isUserAuthorized('OPERATION_LIBRARY') && !isOperationsView}
                    onOperationUnlink={unlinkNodeOperation}
                    onOpenOperationDetail={openOperationInLibrary}
                // Historia 152 (S26/S27 · RN-G5, D10 + D12): el proceso de origen del nodo
                // seleccionado, que es el PAR `(código, versión)`. Es dato DEL NODO y no del vínculo,
                // así que viaja aparte del `operationLink` y lo tienen también los nodos sin vínculo.
                    sourceProcessCode={workflowData.currentNode?.sourceProcessCode || ''}
                    sourceProcessVersionNumber={workflowData.currentNode?.sourceProcessVersionNumber ?? null}
                // La tecnología de un nodo del GRAFO se cambia desde el nodo; una operación de
                // biblioteca no tiene nodo en el lienzo, así que se cambia en sus propiedades.
                    technologyEditable={isOperationsView}
                // La identidad de la operación que se está editando. Lo que se escriba aquí es lo que
                // viaja en el commit (N8): `code`, `name` y `description` del `OperationVersionSaveRequest`.
                    operationIdentity={selectedOperationVersion}
                    onOperationIdentityChange={(patch) => setSelectedOperationVersion((current) => ({ ...current, ...patch }))}
                    taskList={workflowData.currentNode?.jobReference?.taskReferences}
                    technology={workflowData.currentNode?.jobReference?.technology}
                    updateTaskList={(taskReferences) => dispatchWorkflowData({ type: 'UPDATE_TASKS', taskReferences })}
                    editing={jobAccordionEditing}
                    updateJobReference={updateJobReference}
                    // RN117.3 (historia #150): reporta su fila abierta bajo SU id de anfitrion. Sustituye
                    // al `setIsTaskEditing` de develop, un setter de un booleano ajeno que solo el grafo
                    // podia usar: con la tabla montada tambien en el arbol hacian falta varios anfitriones.
                    onEditingRowChanged={(isOpen) => reportOpenRow('graph-job-tasks', isOpen)}
                    progressCalculationTypes={progressCalculationTypes}
                    partReference={workflowData.partReference}
                    // RN01.6/RNT5: JobTask still expects the workflow's functional identifier here (now
                    // workflow.code); the code+name split of node.name (HU-10/S34) is deferred.
                    workflowName={workflowData.workflow?.code}
                    getTempUUID={getTempUUID}
                    setTempUUID={setTempUUID}
                    indexValues={indexValues}
                    workflowUseIndex={workflowUseIndex}
                    showMachineTime={showMachineTime}
                    cascadeApprovalEnabled={cascadeApprovalEnabled}
                    featureGroupRevisionControlled={featureGroupRevisionControlled}
                    featureGroupBaselines={workflowData.featureGroupBaselines}
                // Story 91/RN21: one of the THREE gating factors of the Extern checkbox — the fourth, the
                // technology's type, was removed when the PO revoked D1/D2/D2b on 2026-08-10. It reached
                // WorkflowFlow already but not this branch, so it is threaded on to
                // JobTask -> NodeGeneralProperties, which is where the checkbox lives.
                    fabricationOrderOperable={fabricationOrderOperable}
                    nightJobContext={nightJobContext}
                  />}
            </DefaultAccordion>
            <DefaultAccordion
              headerRender={t_('Preparation')}
              defaultExpanded={false}
              disabled={!workflowData.currentNode || workflowData.currentNode.nodeType === 'WORKFLOW' || !isUserAuthorized('WORKFLOW_JOBS')}
            >
              <NodePreparation
                jobReference={workflowData.currentNode?.jobReference}
                editing={jobAccordionEditing}
                updateJobReference={updateJobReference}
              />
            </DefaultAccordion>
            <DefaultAccordion
              headerRender={(!workflowData.currentNode || workflowData.currentNode.nodeType === 'WORKFLOW') ? t_('BOM') : workflowData.workflow?.hasWorkflowResources ? t_('Resources (BOM)') : t_('Resources')}
              disabled={(workflowData.workflow?.nodes?.length === 0 && !workflowData?.editing) || !isUserAuthorized('RESOURCES')}
              defaultExpanded={false}
              openSignal={resourcesSignal}
            >
              {!workflowData.currentNode || workflowData.currentNode.nodeType === 'WORKFLOW'
                ? (
                  <WorkflowResource
                    workflow={workflowData?.workflow}
                    editing={workflowData?.editing}
                    onHighlightJob={highlightJobNode}
                    onHighlightAssembly={highlightAssemblyNodes}
                    highlightedResourceUUID={highlightedResourceUUID}
                    onClearHighlight={clearJobHighlight}
                    removedResources={removedWorkflowResources}
                    onRemovedResourcesChange={setRemovedWorkflowResources}
                    updateBillOfMaterials={(billOfMaterials) => {
                      dispatchWorkflowData({
                        type: 'UPDATE_WORKFLOW',
                        workflow: {
                          ...workflowData.workflow,
                          billOfMaterials
                        }
                      })
                    }}
                    // Story 117 (S73/S90/S92/S111): the permissions are resolved here, where the rest
                    // of the editor's authorization already lives, and handed down as plain flags.
                    canReadBomLibrary={isUserAuthorized('BOM_LIBRARY') || isUserAuthorized('BOM_LIBRARY_EDIT')}
                    canEditBomLibrary={isUserAuthorized('BOM_LIBRARY_EDIT')}
                    onBomLinkChange={setBomLink}
                    // S71 (corrección PO 2026-09-09): linking is a pending change of the workflow
                    // like any other; `saveWorkflow` is what writes it (N16).
                    pendingBomLink={workflowData.workflow?.pendingBomLink || {}}
                    onBomLinkSelected={(selection) => {
                      markUnsavedChanges()
                      dispatchWorkflowData({
                        type: 'UPDATE_WORKFLOW',
                        workflow: { ...workflowData.workflow, pendingBomLink: selection }
                      })
                    }}
                    updateHasWorkflowResources={(newValue) => { dispatchWorkflowData({ type: 'UPDATE_HAS_WORKFLOW_RESOURCES', hasWorkflowResources: newValue }) }}
                    resetResources={(workflow, importedWorkflowResources, previousWorkflowResources) => {
                      // HU-13/S39-S44 (D2): the code+parent mapping algorithm itself lives in the
                      // pure module `bomImportMapping.js` — this handler is a thin consumer that
                      // only wires the workflow currently being edited and dispatches the result.
                      const newWorkflow = applyBomImportMapping(workflow, previousWorkflowResources, importedWorkflowResources)
                      dispatchWorkflowData({ type: 'UPDATE_WORKFLOW', workflow: newWorkflow })
                    }}
                  />
                  )
                : (
                  <JobResources
                    // Story 124 (S51): a subcontracted JOB carries no resources -- the work happens
                    // outside, so our material is not consumed here. Same treatment the five
                    // planner-facing fields already get: the block goes read-only rather than
                    // disappearing, so what a job used to have stays visible while it is marked.
                    // Sun enforces it on save regardless of what any client sends.
                    editing={jobAccordionEditing && !isExternalEffective(workflowData.currentNode?.jobReference)}
                    resources={workflowData.currentNode ? workflowData.currentNode.jobReference?.resources : []}
                    partReferenceId={workflowData.partReference?.id}
                    onResourcesChanged={(newResources) => {
                      const newNode = { ...workflowData.currentNode, jobReference: { ...workflowData.currentNode.jobReference, resources: newResources } }
                      dispatchWorkflowData({ type: 'UPDATE_CURRENT_NODE', node: newNode })
                    }}
                    resourceTypes={resourceTypes}
                    showAlert={showAlert}
                    parentPartReference={parentPartReference}
                    workflow={workflowData.workflow}
                    // D14/RN117.6: a resource row left open blocks the save exactly as a task row does.
                    onEditingRowChanged={(isOpen) => reportOpenRow('graph-job-resources', isOpen)}
                  />
                  )}
            </DefaultAccordion>
          </div>
        </Stack>
        {/* Enmienda 2 (E2-D1/RN31, CA21): the layer itself, a SIBLING of `editorContentRef`'s wrapper
            (not a descendant — see the comment where that wrapper opens) but still inside the SAME
            `position: relative` ancestor for the absolute positioning. Mounted/unmounted by React
            exactly with `isSaving` (no timeout, no extra state, no reliance on MUI's own open/close
            transition for "gone") — it disappears the moment RN18 releases the buttons, on every exit
            `saveWorkflow` has (RE2-3). zIndex sits above the sticky header (50) and below MUI's
            modals (1300, default), so the derived-save dialogs (RN33/S65-S66) and the D3 conflict
            dialog (which only opens once the save has already finished, RN33) stay reachable. */}
        {isSaving && (
          <Backdrop
            open
            data-testid='workflow-saving-backdrop'
            // MUI sets `aria-hidden` on the Backdrop root by default (it is normally a purely
            // decorative dimming layer) — overridden here because this one carries the ONLY
            // perceivable "Saving" indicator, which has to stay in the accessibility tree.
            slotProps={{ root: { 'aria-hidden': false } }}
            sx={{ position: 'absolute', zIndex: 100, borderRadius: 1 }}
          >
            {/* Same look as the "Saving fabrication order" indicator (`FabricationOrder.jsx`), which
                is a `SmartDialog`. A dialog cannot be used here: it is modal over the whole window,
                and leaving the editor while it saves must stay possible (E2-D2). */}
            <Paper sx={(theme) => ({ background: theme.palette.background.color_373636 + '!important' })}>
              <Stack spacing={1} alignItems='center' justifyContent='center' sx={{ padding: '16px 24px' }}>
                {savingTitle.icon}
                {savingTitle.render}
              </Stack>
              <Divider />
              <Stack spacing={2} alignItems='center' sx={{ padding: '1.5em 2.5em' }}>
                <CircularProgress />
              </Stack>
            </Paper>
          </Backdrop>
        )}
      </Stack>
      {/* S86 (HU-05/CA5.3): the warning that this save creates version n+1 of the linked library
          BOM. Its own dialog, ahead of the normal save dialog, so backing out costs nothing. */}
      <SmartDialog
        setOpen={bomNewVersionWarning}
        title={{ render: <Typography variant='h6'>{t_('A new BOM version will be created')}</Typography> }}
        message={
          <span data-testid='bom-new-version-save-warning'>
            {t_('Version {{version}} of {{code}} will be created and this workflow will point to it', {
              version: nextVersionNumber(bomLink),
              code: bomLink.code
            })}
          </span>
        }
        acceptLabel='Continue'
        acceptCallback={() => { setBomNewVersionWarning(false); setSaveDialogOpen(true) }}
        cancelCallback={() => setBomNewVersionWarning(false)}
        autoClose={false}
      />
      {/* Historia 130 · C9 (CA2.3) y C11 (S11), ambos propios de la vista OPERATIONS. */}
      {operationSaveMode
        ? (
          <OperationSaveModeDialog
            open
            allowedSaveModes={operationSaveMode.allowedSaveModes}
            indexOptions={indexValues}
            defaultIndexValue={operationSaveMode.defaultIndexValue}
            error={operationSaveMode.error}
            useIndex={!!workflowUseIndex}
            onConfirm={commitOperationVersion}
            onClose={() => setOperationSaveMode(null)}
          />
          )
        : null}
      <OperationPendingChangesDialog
        open={!!pendingVersionChange}
        onSave={startOperationSave}
        onDiscard={async () => {
          const next = pendingVersionChange
          setPendingVersionChange(null)
          await revertOperationScope()
          await loadOperationVersion(next)
        }}
        onCancel={() => setPendingVersionChange(null)}
      />
      <WorkflowSaveDialogs
        open={saveDialogOpen}
        setOpen={setSaveDialogOpen}
        onSave={saveWorkflow}
        onCancel={() => setSaveDialogOpen(false)}
        workflow={workflowData.workflow}
        canPropagate={workflowData.partReference}
        indexValues={indexValues}
        workflowUseIndex={workflowUseIndex}
        cascadeApprovalEnabled={cascadeApprovalEnabled}
        featureGroupRevisionControlled={featureGroupRevisionControlled}
        savedWorkflowIndexIsApproved={originalData.indexValue?.name === 'APPROVED'}
        isSaving={isSaving}
        beginSave={beginSave}
        endSave={endSave}
        showAlert={showAlert}
      />
      <WorkflowVersionConflictDialog
        open={workflowVersionConflict.open}
        onReload={reloadAfterWorkflowVersionConflict}
        onStay={stayInEditorAfterWorkflowVersionConflict}
      />
    </Stack>
  )
}

const isObjectForDiff = (value) => value !== null && typeof value === 'object'

/**
 * Historia 130 (S7/S11): las acciones de los cuatro acordeones cuando lo que se edita es una versión
 * de la biblioteca de operaciones y no un nodo del grafo.
 */
/**
 * Historia 130 (S55/S56/S61): la foto del `jobReference` de cada nodo VINCULADO tal y como llega del
 * servidor. `operationLinkIntegrity.js` la usa de referencia para adelantar en cliente si lo que el
 * usuario acaba de cambiar rompe el vínculo. Un revert recarga el workflow y con él estas fotos, y
 * por eso el nodo vuelve a mostrarse vinculado sin preguntar a nadie (S61).
 *
 * Historia 152 (S21 · impacto R5): el recorrido es RECURSIVO por los subworkflows ya hidratados. La
 * documentación de #130 avisaba de que "cualquier otra vía por la que un nodo vinculado entre en el
 * workflow lo dejará sin foto", y CA1.4 —soltar un proceso entero— es exactamente esa vía: sus
 * nodos vinculados viven un nivel más abajo. Sin esto, editarlos no se detecta en cliente y la
 * cabecera miente hasta el guardado.
 */
const collectOperationLinkBaselines = (workflow) => {
  const baselines = {}
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      if (node?.operationLink?.linked && node.jobReference) {
        baselines[node.id] = structuredClone(node.jobReference)
      }
      walk(node?.subWorkflow?.nodes)
    })
  }
  walk(workflow?.nodes)
  return baselines
}

/**
 * Historia 130: lo que la cabecera del nodo debe pintar — el vínculo tal y como está escrito (N13)
 * con la intención pendiente del editor encima (Δ9: BIND, UPDATE y PUBLISH no se escriben hasta el
 * guardado general). Sin la fusión, aceptar una actualización no cambiaba nada en pantalla.
 */
const resolveNodeOperationLink = (persisted, pending) => {
  if (pending?.intent) {
    return { ...(persisted || {}), ...pending }
  }
  // Historia 152 (S26 · RN-01.18): la fila persistida manda también cuando está DESVINCULADA. Con el
  // criterio de #130 —"solo si `linked`"— un nodo desvinculado caía al bloque del nodo y el panel lo
  // daría por embebido, borrando en pantalla una procedencia que el servidor sí conserva.
  return (persisted?.linked || persisted?.state) ? persisted : (pending || {})
}

const OPERATION_NODE_ACTIONS = [
  'SET_CURRENT_NODE', 'UPDATE_CURRENT_NODE', 'UPDATE_TASKS', 'UPDATE_JOB_REFERENCE', 'OPERATION_SCOPE_SETTLED'
]

const reduceOperationNodeAction = (oldData, action) => {
  // Seleccionar una versión no es un cambio pendiente: solo carga lo que se va a mirar o editar.
  if (action.type === 'SET_CURRENT_NODE') {
    return { ...oldData, currentNode: action.node }
  }
  // El ámbito de edición se ha resuelto (commit N8 o revert N9): ya no hay nada pendiente.
  if (action.type === 'OPERATION_SCOPE_SETTLED') {
    return { ...oldData, operationPendingChanges: false }
  }
  if (!oldData.currentNode) {
    return oldData
  }
  const currentNode = { ...oldData.currentNode }
  if (action.type === 'UPDATE_CURRENT_NODE') {
    return { ...oldData, currentNode: { ...action.node }, operationPendingChanges: true }
  }
  if (action.type === 'UPDATE_TASKS') {
    currentNode.jobReference = {
      ...currentNode.jobReference,
      taskReferences: action.taskReferences.map((task, index) => ({ ...task, taskOrder: index + 1 }))
    }
    return { ...oldData, currentNode, operationPendingChanges: true }
  }
  currentNode.jobReference = action.jobReference
  return { ...oldData, currentNode, operationPendingChanges: true }
}

export default Workflow
